// =============================================================================
// FILE: packages/renderer/src/lib/api.ts
// PURPOSE: Typed API client. In Electron mode it speaks IPC over contextBridge.
//          In hybrid web mode (no preload) it falls back to a built-in mock so
//          the renderer always renders something useful for design preview.
// =============================================================================

import { IPC, type IPCChannel } from '@bim/shared/constants/ipc.constants.js';
import {
  isDemoMode, SIERRA_MANOR_STATS, SIERRA_MANOR_PROJECT_NAME, sierraManorActivity,
} from '../config/demoMode.js';

/** Returns the email of the currently authenticated user, or a safe fallback. */
function currentUserEmail(): string {
  try {
    // Dynamic import to avoid circular deps — Zustand getState() is sync
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { useAuthStore } = require('../stores/auth.store.js') as typeof import('../stores/auth.store.js');
    return useAuthStore.getState().user?.email ?? 'system';
  } catch {
    return 'system';
  }
}

export interface IPCError {
  readonly code: string;
  readonly message: string;
  readonly fields?: Record<string, string[]>;
}

export interface IPCResponse<T> {
  readonly success: boolean;
  readonly data?: T;
  readonly error?: IPCError;
}

interface BimApi {
  invoke<TInput, TOutput>(channel: string, payload: { token?: string; data?: TInput }): Promise<IPCResponse<TOutput>>;
  on<T>(channel: string, listener: (payload: T) => void): () => void;
  platform: NodeJS.Platform | string;
  versions: NodeJS.ProcessVersions | Record<string, string>;
}

declare global {
  interface Window {
    api?: BimApi;
  }
}

// ---------------------------------------------------------------------------
// Token storage — localStorage in renderer
// ---------------------------------------------------------------------------
const TOKEN_KEY = 'bim.access_token';
const REFRESH_KEY = 'bim.refresh_token';

export const tokens = {
  get(): string | null { return localStorage.getItem(TOKEN_KEY); },
  getRefresh(): string | null { return localStorage.getItem(REFRESH_KEY); },
  set(access: string, refresh: string): void {
    localStorage.setItem(TOKEN_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

// ---------------------------------------------------------------------------
// Typed call helper
// ---------------------------------------------------------------------------
export class IpcCallError extends Error {
  public readonly code: string;
  public readonly fields?: Record<string, string[]>;
  constructor(err: IPCError) {
    super(err.message);
    this.code = err.code;
    this.fields = err.fields;
  }
}

export const isElectron = (): boolean => typeof window !== 'undefined' && !!window.api;

// In a packaged Electron build the userAgent contains "Electron". If we are
// inside Electron but window.api is missing, the preload bridge failed to
// load — show that loudly instead of silently falling back to demo mocks
// (which produced the long-standing "reset shows PRJ-2026-001 again" bug).
//
// EXCEPTION: The minimal demo wrapper sets window.r2DemoMode = true via its
// own preload. In that case, we are intentionally running in web-mock mode
// inside Electron, so we must NOT throw the PRELOAD_BRIDGE_MISSING error.
const isInsideElectronShell = (): boolean => {
  if (typeof window !== 'undefined'
      && (window as unknown as Record<string, unknown>)['r2DemoMode'] === true) {
    return false;
  }
  return typeof navigator !== 'undefined' && /Electron/i.test(navigator.userAgent);
};

// ---------------------------------------------------------------------------
// Debug tracing — enable by setting `localStorage.bim.debug = '1'` in the
// renderer DevTools console, or by setting `process.env.BIM_DEBUG_IPC=1` in
// the main process (mirror-mode). Off by default, free in production.
// ---------------------------------------------------------------------------
function isDebugIpcEnabled(): boolean {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('bim.debug') === '1') return true;
  } catch { /* SSR / no localStorage */ }
  return false;
}

let _traceSeq = 0;

export async function call<TInput, TOutput>(
  channel: IPCChannel,
  data?: TInput,
): Promise<TOutput> {
  const api = window.api;
  const trace = isDebugIpcEnabled();
  const traceId = trace ? `ipc#${++_traceSeq}` : '';
  const t0 = trace ? performance.now() : 0;
  if (trace) {
    // eslint-disable-next-line no-console
    console.info(`[BIM IPC ▶ ${traceId}] ${channel}`,
      { hasToken: !!tokens.get(), data });
  }

  // Web (no Electron) — fallback to mock
  if (!api) {
    // Inside the packaged Electron shell, missing window.api means the
    // preload bridge failed to load. Refuse to serve demo mocks — surface
    // a hard error so the symptom is obvious instead of looking like the
    // database "won't reset".
    if (isInsideElectronShell()) {
      const err = new IpcCallError({
        code: 'PRELOAD_BRIDGE_MISSING',
        message:
          `Electron preload bridge did not expose window.api for channel "${channel}". ` +
          `The renderer is running inside the .exe shell but cannot reach the main process. ` +
          `Check packages/main/dist/preload.cjs and the webPreferences.preload path in window.ts.`,
      });
       
      console.error('[BIM IPC] Preload bridge missing — refusing to use demo mocks.', err);
      throw err;
    }
    try {
      const out = await mockCall<TOutput>(channel, data);
      if (trace) {
        // eslint-disable-next-line no-console
        console.info(`[BIM IPC ◀ ${traceId}] ${channel} (mock) ${Math.round(performance.now() - t0)}ms`, out);
      }
      return out;
    } catch (err) {
      if (trace) {
         
        console.warn(`[BIM IPC ✗ ${traceId}] ${channel} (mock) ${Math.round(performance.now() - t0)}ms`, err);
      }
      throw err;
    }
  }

  const token = tokens.get() ?? undefined;
  const response = await api.invoke<TInput, TOutput>(channel, { token, data });

  if (!response.success) {
    const err = new IpcCallError(
      response.error ?? { code: 'INTERNAL_ERROR', message: 'Unknown IPC error' });
    if (trace) {
       
      console.warn(`[BIM IPC ✗ ${traceId}] ${channel} ${Math.round(performance.now() - t0)}ms`,
        { code: err.code, message: err.message, fields: err.fields });
    }
    // Specific signal for renderer-side health probe: missing handler in main.
    if (err.code === 'NOT_FOUND' || /no handler/i.test(err.message)) {
       
      console.error(
        `[BIM IPC] No main-process handler registered for "${channel}". ` +
        `This is a wiring bug, not a runtime failure. Check bootstrap.ts.`,
      );
    }
    throw err;
  }

  if (trace) {
    // eslint-disable-next-line no-console
    console.info(`[BIM IPC ◀ ${traceId}] ${channel} ${Math.round(performance.now() - t0)}ms`, response.data);
  }
  return response.data as TOutput;
}

// ---------------------------------------------------------------------------
// Mock fallback — keeps the UI lively during pure-web preview
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Project Live Notes — web-preview mock store. Keyed by projectId.
// ---------------------------------------------------------------------------
interface MockProjectLogEntry {
  id: string; projectId: string; timestamp: number; author: string; message: string;
}
const __projectLogsMock: Record<string, MockProjectLogEntry[]> = {};

interface MockProjectRow {
  id: string; projectNumber: string; name: string; client: string;
  clientAddress: string | null; phase: string; status: string;
  color?: string;
}
// Single seed project so the web-preview testing harness + designers can
// inspect the Project Live Notes + Meeting Capture UI without first having
// to fill out the create-project modal. The seed is intentionally minimal
// (zero notes, zero meetings) so the empty-state UI is observable.
const __projectsMock: MockProjectRow[] = [
  {
    id:            'mock-proj-preview',
    projectNumber: 'PREVIEW-001',
    name:          'Web Preview Sandbox',
    client:        'RCD2 Inc.',
    clientAddress: null,
    phase:         'planning',
    status:        'active',
  },
];


// ---------------------------------------------------------------------------
// Folder Monitor — web-preview mock (Electron preload provides the real impl).
// ---------------------------------------------------------------------------
interface MockFolderEvent {
  id: string; kind: 'created' | 'modified' | 'deleted';
  path: string; relPath: string; sizeBytes: number | null;
  mtimeMs: number | null; at: string;
}
interface MockFolderMonitorState {
  folder: string | null; active: boolean; startedAt: string | null;
  eventCount: number; lastEvent: MockFolderEvent | null;
  accessible: boolean; accessError: string | null;
  events: MockFolderEvent[];
}
let __folderMonitorMock: MockFolderMonitorState | null = null;
let __folderMonitorMockTimer: ReturnType<typeof setInterval> | null = null;
function ensureFolderMonitorMock(): MockFolderMonitorState {
  if (!__folderMonitorMock) {
    __folderMonitorMock = {
      folder: null, active: false, startedAt: null,
      eventCount: 0, lastEvent: null,
      accessible: true, accessError: null, events: [],
    };
  }
  return __folderMonitorMock;
}
function emitMockFolderEvent(): void {
  const s = ensureFolderMonitorMock();
  if (!s.active || !s.folder) return;
  const kinds: MockFolderEvent['kind'][] = ['created', 'modified', 'deleted'];
  const kind = kinds[Math.floor(Math.random() * kinds.length)]!;
  const samples = [
    'Revit/Model-Central.rvt', 'Submittals/RFI-024.pdf',
    'Photos/IMG-0312.jpg', 'AutoCAD/Site-Plan.dwg',
    'Reports/Weekly-Status.docx',
  ];
  const rel = samples[Math.floor(Math.random() * samples.length)]!;
  const evt: MockFolderEvent = {
    id: `mock-${Date.now()}-${Math.floor(Math.random() * 1e4)}`,
    kind, relPath: rel, path: `${s.folder}/${rel}`,
    sizeBytes: kind === 'deleted' ? null : Math.floor(Math.random() * 5_000_000),
    mtimeMs: Date.now(), at: new Date().toISOString(),
  };
  s.events = [evt, ...s.events].slice(0, 100);
  s.eventCount += 1; s.lastEvent = evt;
}
function startFolderMonitorMockTimer(): void {
  if (__folderMonitorMockTimer) return;
  __folderMonitorMockTimer = setInterval(emitMockFolderEvent, 4_000);
}
function stopFolderMonitorMockTimer(): void {
  if (__folderMonitorMockTimer) clearInterval(__folderMonitorMockTimer);
  __folderMonitorMockTimer = null;
}


// ---------------------------------------------------------------------------
// B6 — Agent Projects web-preview mock. Composes the autopilot mock
// store under the hood — same way the main service does.
// ---------------------------------------------------------------------------
interface MockGoal {
  id: string; tenantId: string; actorId: string;
  projectId: string | null; templateKey: string | null;
  goalText: string;
  status: 'proposed' | 'running' | 'awaiting_approval' | 'done' | 'failed' | 'cancelled';
  planId: string | null;
  cancelRequested: boolean;
  createdAt: string; updatedAt: string;
}
interface MockEvent {
  id: string; ordinal: number;
  kind: 'plan_created' | 'step_started' | 'step_done' | 'step_failed'
      | 'awaiting_approval' | 'cancel_requested' | 'goal_done' | 'goal_failed'
      | 'goal_cancelled' | 'capability_blocked';
  actionId: string | null; message: string; detail: unknown | null; createdAt: string;
}
const mockAG: {
  goals: MockGoal[];
  events: Map<string, MockEvent[]>;   // goalId -> events
  seq: number;
} = { goals: [], events: new Map(), seq: 1 };

const MOCK_AGENT_TEMPLATES = [
  { key: 'find-overdue-rfis',     title: 'Find overdue RFIs and draft follow-ups',
    description: 'Scans every open RFI past the threshold and drafts B4 follow-up emails for operator review.',
    defaultGoal: 'Find overdue RFIs and draft a follow-up for each one.',
    projectScoped: true },
  { key: 'this-weeks-followups',  title: "Prepare this week's follow-ups",
    description: 'Drafts every B4 follow-up the suggest-mode engine would propose, queues task nudges for overdue work.',
    defaultGoal: "Prepare this week's outstanding follow-ups across all enabled signals.",
    projectScoped: true },
  { key: 'project-health-review', title: 'Project health review',
    description: 'Builds a snapshot of every overdue / stalled signal on the project and queues remediation drafts.',
    defaultGoal: 'Review project health and queue follow-up drafts + task nudges where needed.',
    projectScoped: true },
  { key: 'tomorrows-workload',    title: "Prepare tomorrow's workload",
    description: "Surfaces what's about to slip (overdue + nearly-overdue tasks) and drafts owner nudges.",
    defaultGoal: "Prepare tomorrow's workload — surface what's about to slip and queue owner nudges.",
    projectScoped: true },
];

function appendMockEvent(
  goalId: string,
  kind: MockEvent['kind'],
  message: string,
  detail?: unknown,
  actionId?: string | null,
): MockEvent {
  const list = mockAG.events.get(goalId) ?? [];
  const ord = list.length;
  const ev: MockEvent = {
    id: `mock-evt-${mockAG.seq++}`,
    ordinal: ord,
    kind,
    actionId: actionId ?? null,
    message,
    detail: detail ?? null,
    createdAt: new Date().toISOString(),
  };
  list.push(ev);
  mockAG.events.set(goalId, list);
  return ev;
}

// ---------------------------------------------------------------------------
// B5 — Autopilot web-preview mock store. Plan-only surface; no execution.
// ---------------------------------------------------------------------------
interface MockAction {
  id: string; planId: string; tenantId: string; ordinal: number;
  capability: string; title: string; description: string;
  payload: Record<string, unknown>;
  status: 'proposed' | 'approved' | 'rejected' | 'running' | 'done' | 'failed' | 'blocked_elevation';
  outcome: unknown | null;
  createdAt: string; updatedAt: string;
}
interface MockPlan {
  id: string; tenantId: string; projectId: string | null; actorId: string;
  goal: string; rationale: string;
  status: 'proposed' | 'partially_approved' | 'completed' | 'cancelled';
  actions: MockAction[];
  createdAt: string; updatedAt: string;
}
const mockAP: { plans: MockPlan[]; seq: number } = { plans: [], seq: 1 };

function mockProposeActions(projectId: string | null): Omit<MockAction,
  'id' | 'planId' | 'tenantId' | 'createdAt' | 'updatedAt' | 'status' | 'outcome'>[] {
  if (!projectId) return [];
  return [
    { ordinal: 0, capability: 'email.draft',
      title: `Draft 'Overdue task follow-up' for project ${projectId}`,
      description: "Drafts a polite follow-up for every overdue task on the project.",
      payload: { triggerKey: 'overdue-task-followup', defaultRecipient: { email: 'pm@local' } } },
    { ordinal: 1, capability: 'email.draft',
      title: `Draft 'Overdue RFI follow-up' for project ${projectId}`,
      description: "Drafts a nudge for every RFI open beyond the threshold.",
      payload: { triggerKey: 'overdue-rfi-followup', defaultRecipient: { email: 'pm@local' } } },
    { ordinal: 2, capability: 'task.nudge',
      title: 'Nudge owner of task T-101',
      description: 'Send an internal reminder to the owner of overdue task T-101.',
      payload: { taskId: 'T-101' } },
    { ordinal: 3, capability: 'task.nudge',
      title: 'Nudge owner of task T-102',
      description: 'Send an internal reminder to the owner of overdue task T-102.',
      payload: { taskId: 'T-102' } },
  ];
}

function describeMockAction(a: MockAction): string {
  if (a.capability === 'email.draft') {
    const k = (a.payload as { triggerKey?: string }).triggerKey ?? 'unknown';
    return `Generate a B4 suggest-mode draft via the '${k}' trigger. Drafts land in the operator review queue — sending still requires the email.send capability + tenant kill-switch check.`;
  }
  if (a.capability === 'task.nudge') {
    const t = (a.payload as { taskId?: string }).taskId ?? 'unknown';
    return `Record a stub nudge for overdue task '${t}'. Real task mutation pending Track A ProjectCommandPort.`;
  }
  return `Run capability '${a.capability}'.`;
}
interface MockDraft {
  id: string; tenantId: string; projectId: string | null;
  triggerKey: string; subject: string; body: string;
  recipients: Array<{ email: string; name?: string }>;
  attachments: Array<{ ref: string }>;
  status: 'draft' | 'approved' | 'sent' | 'discarded' | 'send_failed';
  contentHash: string; providerRef: string | null;
  createdBy: string; approvedBy: string | null; sentAt: string | null;
  createdAt: string; updatedAt: string;
}
const MOCK_B4_REGISTERED = [
  { key: 'overdue-task-followup',          title: 'Overdue task follow-up',
    description: 'Drafts a polite follow-up for every overdue task on the project.' },
  { key: 'overdue-rfi-followup',           title: 'Overdue RFI follow-up',
    description: 'Drafts a nudge for every RFI that has been open beyond the threshold.' },
  { key: 'stalled-required-info-followup', title: 'Stalled required-info follow-up',
    description: 'Drafts a check-in when required info is missing/pending past the review window.' },
  { key: 'pre-closeout-milestone-checkin', title: 'Pre-closeout milestone check-in',
    description: 'Drafts a single check-in when a project crosses 80% but is not yet in closeout.' },
];
const mockB4: {
  drafts: MockDraft[];
  triggerEnabled: Record<string, boolean>;
  killSwitchOn: boolean;
  seq: number;
} = {
  drafts: [],
  triggerEnabled: Object.fromEntries(MOCK_B4_REGISTERED.map((t) => [t.key, true])),
  killSwitchOn: false,
  seq: 1,
};
function mockHash(s: string): string {
  let h = 0; for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return `${Math.abs(h).toString(16).padStart(8, '0')}${'0'.repeat(56)}`;
}
function mockCreateDraft(input: { projectId: string; triggerKey: string;
  subject: string; body: string;
  defaultRecipient: { email: string; name?: string }; }): MockDraft {
  const id = `mock-draft-${mockB4.seq++}`;
  const now = new Date().toISOString();
  const draft: MockDraft = {
    id, tenantId: 'default', projectId: input.projectId,
    triggerKey: input.triggerKey, subject: input.subject, body: input.body,
    recipients: [input.defaultRecipient], attachments: [],
    status: 'draft', contentHash: mockHash(input.subject + '\n' + input.body),
    providerRef: null, createdBy: 'demo', approvedBy: null, sentAt: null,
    createdAt: now, updatedAt: now,
  };
  mockB4.drafts.unshift(draft);
  return draft;
}

// ---------------------------------------------------------------------------
// B8 — Company Cost Templates web-preview mock store.
// ---------------------------------------------------------------------------
interface MockCostTemplate {
  id: string; templateKey: string; tenantId: string; version: number;
  name: string; description: string; region: string;
  estimateClass: string; isActive: boolean;
  createdBy: string; createdAt: string;
   
  snapshot: any;
}
interface MockEstimateRecord {
  id: string; tenantId: string; projectId: string | null;
  templateVersionId: string;
   
  scope: any;
   
  result: any;
  estimateClass: string; totalCents: number; aiAssisted: boolean;
  qaStatus: 'unchecked' | 'pending' | 'pass' | 'warn' | 'fail';
  qaReportRef: string | null;
  createdBy: string; createdAt: string;
}
interface MockProjectBinding {
  id: string; tenantId: string; projectId: string;
  templateVersionId: string;
  boundBy: string; boundAt: string;
  unboundAt: string | null;
}
const mockB8: {
  templates: MockCostTemplate[];
  estimates: MockEstimateRecord[];
  bindings:  MockProjectBinding[];
  seq: number;
} = {
  templates: [
    {
      id: 'mock-tpl-seed-1',
      templateKey: 'ellisdon-default',
      tenantId: 'default', version: 1,
      name: 'EllisDon Defaults — Region East-Canada',
      description: 'Starter template: union labor + standard ICT materials. '
                 + 'Edit to fork v2 with company-specific rates.',
      region: 'east-canada', estimateClass: 'ROM',
      isActive: true,
      createdBy: 'demo',
      createdAt: new Date(Date.now() - 7 * 86_400_000).toISOString(),
      snapshot: {
        schemaVersion: 1, templateKey: 'ellisdon-default', version: 1,
        name: 'EllisDon Defaults — Region East-Canada',
        description: 'Starter template: union labor + standard ICT materials.',
        region: 'east-canada', estimateClass: 'ROM',
        labor: [
          { trade: 'electrician', hourlyCents: 10_000, burdenPct: 0.22 },
          { trade: 'labourer',    hourlyCents:  5_500, burdenPct: 0.18 },
          { trade: 'low-voltage-tech', hourlyCents: 8_500, burdenPct: 0.20 },
        ],
        materials: [
          { code: 'conduit-3/4', unit: 'lf', unitCents: 200, wasteFactor: 0.05 },
          { code: 'wire-12awg',  unit: 'lf', unitCents: 75,  wasteFactor: 0.10 },
          { code: 'cat6-cable',  unit: 'lf', unitCents: 65,  wasteFactor: 0.08 },
        ],
        equipment: [
          { code: 'lift-19ft', unit: 'day', unitCents: 25_000 },
          { code: 'fusion-splicer', unit: 'day', unitCents: 45_000 },
        ],
        subcontractors: [
          { trade: 'low-voltage', unit: 'ls', unitCents: 1_000_000 },
        ],
        markup: {
          overheadPct: 0.10, contingencyPct: 0.05,
          escalationPct: 0.02, taxesPct: 0.13, profitPct: 0.08,
        },
        rulePack: {},
        currency: 'CAD',
      },
    },
    {
      id: 'mock-tpl-seed-2',
      templateKey: 'rcd2-tier1-datacenter',
      tenantId: 'default', version: 1,
      name: 'RCD2 Tier-1 Datacenter Build',
      description: 'High-density datacenter cabling + power. Conservative '
                 + 'wastes, separate subcontractor for fibre splicing.',
      region: 'default', estimateClass: 'C',
      isActive: true,
      createdBy: 'demo',
      createdAt: new Date(Date.now() - 14 * 86_400_000).toISOString(),
      snapshot: {
        schemaVersion: 1, templateKey: 'rcd2-tier1-datacenter', version: 1,
        name: 'RCD2 Tier-1 Datacenter Build',
        description: 'High-density datacenter cabling + power.',
        region: 'default', estimateClass: 'C',
        labor: [
          { trade: 'electrician', hourlyCents: 11_500, burdenPct: 0.25 },
          { trade: 'cabling-tech', hourlyCents: 9_500, burdenPct: 0.22 },
        ],
        materials: [
          { code: 'fibre-om4-12f', unit: 'lf', unitCents: 480, wasteFactor: 0.06 },
          { code: 'lc-lc-patch',   unit: 'each', unitCents: 1_800, wasteFactor: 0.02 },
        ],
        equipment: [
          { code: 'otdr-rental', unit: 'day', unitCents: 35_000 },
        ],
        subcontractors: [
          { trade: 'fibre-splicing', unit: 'ls', unitCents: 1_750_000 },
        ],
        markup: {
          overheadPct: 0.12, contingencyPct: 0.08,
          escalationPct: 0.03, taxesPct: 0.13, profitPct: 0.10,
        },
        rulePack: { categoryMinimumsCents: { profit: 50_000 } },
        currency: 'USD',
      },
    },
  ],
  estimates: [],
  bindings: [],
  seq: 100,
};

function mockB8Lineages(): MockCostTemplate[] {
  // latest version per templateKey
  const seen = new Set<string>();
  const out: MockCostTemplate[] = [];
  for (const t of [...mockB8.templates].sort((a, b) => b.version - a.version)) {
    if (seen.has(t.templateKey)) continue;
    seen.add(t.templateKey); out.push(t);
  }
  return out;
}

function mockB8ComputeEstimate(
  tpl: MockCostTemplate,
  scope: { estimateClass?: string; notes?: string;
    lines: Array<{ ref: string; category: 'labor'|'material'|'equipment'|'subcontractor';
      identifier: string; quantity: number; aiAssisted?: boolean }>; },
  projectId: string | null,
) {
  const warnings: string[] = [];
   
  const snap = tpl.snapshot as any;
  const lineCents = (l: { category: string; identifier: string; quantity: number }) => {
    if (l.category === 'labor') {
       
      const r = snap.labor.find((x: any) => x.trade === l.identifier);
      if (!r) { warnings.push(`No labor rate for trade='${l.identifier}'`); return 0; }
      return Math.round(l.quantity * r.hourlyCents * (1 + Math.max(0, r.burdenPct)));
    }
    if (l.category === 'material') {
       
      const r = snap.materials.find((x: any) => x.code === l.identifier);
      if (!r) { warnings.push(`No material rate for code='${l.identifier}'`); return 0; }
      const w = 1 + Math.max(0, Math.min(0.5, r.wasteFactor));
      return Math.round(l.quantity * r.unitCents * w);
    }
    if (l.category === 'equipment') {
       
      const r = snap.equipment.find((x: any) => x.code === l.identifier);
      if (!r) { warnings.push(`No equipment rate for code='${l.identifier}'`); return 0; }
      return Math.round(l.quantity * r.unitCents);
    }
    // subcontractor
     
    const r = snap.subcontractors.find((x: any) => x.trade === l.identifier);
    if (!r) { warnings.push(`No subcontractor rate for trade='${l.identifier}'`); return 0; }
    return Math.round(l.quantity * r.unitCents);
  };
  const cat = (
    category: 'labor'|'material'|'equipment'|'subcontractor',
  ): {
    category: string; subtotalCents: number;
    detail: Array<{ ref: string; identifier: string; quantity: number; cents: number }>;
  } => {
    const detail: Array<{ ref: string; identifier: string; quantity: number; cents: number }> = [];
    let subtotal = 0;
    for (const l of scope.lines) if (l.category === category) {
      const cents = lineCents(l);
      detail.push({ ref: l.ref, identifier: l.identifier, quantity: l.quantity, cents });
      subtotal += cents;
    }
    return { category, subtotalCents: subtotal, detail };
  };
  const labor = cat('labor');
  const matl  = cat('material');
  const equip = cat('equipment');
  const sub   = cat('subcontractor');
  // Apply minimums.
   
  const mins = (snap.rulePack?.categoryMinimumsCents ?? {}) as Record<string, number>;
  for (const c of [labor, matl, equip, sub]) {
    const m = mins[c.category];
    if (typeof m === 'number' && c.subtotalCents < m) {
      warnings.push(`Category '${c.category}' clamped to minimum ${m}`); c.subtotalCents = m;
    }
  }
  const direct = labor.subtotalCents + matl.subtotalCents + equip.subtotalCents + sub.subtotalCents;
  const mkMk = (name: string, pct: number, base: number) => ({
    category: name, subtotalCents: Math.round(base * Math.max(0, pct)),
    detail: [{ ref: `markup:${name}`, identifier: `${(pct * 100).toFixed(2)}%`,
              quantity: 1, cents: Math.round(base * Math.max(0, pct)) }],
  });
  let running = direct;
  const overhead    = mkMk('overhead',    snap.markup.overheadPct,    running); running += overhead.subtotalCents;
  const contingency = mkMk('contingency', snap.markup.contingencyPct, running); running += contingency.subtotalCents;
  const escalation  = mkMk('escalation',  snap.markup.escalationPct,  running); running += escalation.subtotalCents;
  const taxes       = mkMk('taxes',       snap.markup.taxesPct,       running); running += taxes.subtotalCents;
  const profit      = mkMk('profit',      snap.markup.profitPct,      running); running += profit.subtotalCents;
  const totalCents  = running;
  const aiAssisted  = scope.lines.some((l) => l.aiAssisted === true);
  const estimate = {
    templateVersionId: `${snap.templateKey}@${snap.version}`,
    templateKey:       snap.templateKey,
    templateVersion:   snap.version,
    estimateClass:     scope.estimateClass ?? snap.estimateClass,
    currency:          snap.currency,
    categories:        [labor, matl, equip, sub, overhead, contingency, escalation, taxes, profit],
    directCents:       direct,
    totalCents,
    aiAssisted,
    warnings,
  };
  const record: MockEstimateRecord = {
    id: `mock-est-${mockB8.seq++}`,
    tenantId: 'default',
    projectId,
    templateVersionId: tpl.id,
    scope,
    result: estimate,
    estimateClass: estimate.estimateClass as string,
    totalCents, aiAssisted,
    qaStatus: 'unchecked',
    qaReportRef: null,
    createdBy: 'demo',
    createdAt: new Date().toISOString(),
  };
  return { estimate, record };
}

function mockB8Hash(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function mockB8Suggest(tpl: MockCostTemplate, goalText: string) {
   
  const snap = tpl.snapshot as any;
  const goal = goalText.toLowerCase();
  const matched: string[] = [];
   
  const lines: Array<{ ref: string; category: any; identifier: string; quantity: number; aiAssisted: boolean }> = [];
  let idx = 0;
  const seedBase = `${snap.templateKey}@${snap.version}|${goalText}`;
  const qty = (seed: string) => 1 + (mockB8Hash(seed) % 25);
   
  const push = (category: any, identifier: string) => {
    const ref = `ai-local-stub-${idx++}`;
    lines.push({ ref, category, identifier,
      quantity: qty(`${seedBase}|${category}|${identifier}|${idx}`), aiAssisted: true });
    matched.push(`${category}:${identifier}`);
  };
   
  for (const r of snap.labor)          if (goal.includes(String(r.trade).toLowerCase())) push('labor',         r.trade);
   
  for (const r of snap.materials)      if (goal.includes(String(r.code).toLowerCase()))  push('material',      r.code);
   
  for (const r of snap.equipment)      if (goal.includes(String(r.code).toLowerCase()))  push('equipment',     r.code);
   
  for (const r of snap.subcontractors) if (goal.includes(String(r.trade).toLowerCase())) push('subcontractor', r.trade);
  let fallback = false;
  if (lines.length === 0) {
    fallback = true;
    if (snap.labor[0])          push('labor',         snap.labor[0].trade);
    if (snap.materials[0])      push('material',      snap.materials[0].code);
    if (snap.equipment[0])      push('equipment',     snap.equipment[0].code);
    if (snap.subcontractors[0]) push('subcontractor', snap.subcontractors[0].trade);
  }
  return {
    lines,
    rationale: fallback
      ? `No template entries matched the goal "${goalText}". `
        + `Suggested one starter line per category from template `
        + `${snap.templateKey}@${snap.version}.`
      : `Matched ${matched.length} template entr${matched.length === 1 ? 'y' : 'ies'} `
        + `from "${goalText}": ${matched.join(', ')}.`,
    providerKey: 'local-stub',
    displayName: 'Local stub (offline)',
    disclaimer:  'Estimated — User Review Required. AI-assisted suggestions are starting '
               + 'points only. Every line item, quantity, and rate must be verified by a '
               + 'qualified estimator before issuing a quote, change order, or commitment.',
    confidence:  'estimate',
  };
}

// ---------------------------------------------------------------------------
// B9 — Backup mock store and handler.
// ---------------------------------------------------------------------------
interface MockSchedule {
  id: string; tenantId: string; destinationKey: string;
  cadence: 'hourly' | 'four-hourly' | 'daily' | 'weekly';
  enabled: boolean;
  retentionCount: number; retentionAgeDays: number;
  lastRunAt: string | null; nextRunAt: string | null;
  createdAt: string; updatedAt: string;
}
interface MockSnapshot {
  id: string; tenantId: string; destinationKey: string;
  kind: 'full' | 'incremental';
  sizeBytes: number; sha256: string;
  encryption: 'aes-256-gcm' | 'none';
  storageRef: string;
  verifiedAt: string | null; verifyError: string | null;
  schedulesId: string | null;
  createdBy: string; createdAt: string;
}
interface MockBkpEvent {
  id: string; tenantId: string; ordinal: number;
  destinationKey: string; snapshotId: string | null;
  kind: string; message: string;
  detail: unknown | null; actorId: string; createdAt: string;
}
const mockB9: {
  destinations: Array<{ key: string; displayName: string }>;
  schedules: MockSchedule[];
  snapshots: MockSnapshot[];
  events:    MockBkpEvent[];
  seq:       number;
} = {
  destinations: [
    { key: 'local-encrypted', displayName: 'Local encrypted (filesystem)' },
  ],
  schedules: [], snapshots: [], events: [], seq: 1,
};
const CADENCE_MS_RENDERER: Record<MockSchedule['cadence'], number> = {
  'hourly':       60 * 60 * 1000,
  'four-hourly':  4 * 60 * 60 * 1000,
  'daily':        24 * 60 * 60 * 1000,
  'weekly':       7 * 24 * 60 * 60 * 1000,
};

function mockB9Sha(): string {
  let s = '';
  const hex = '0123456789abcdef';
  for (let i = 0; i < 64; i++) s += hex[Math.floor(Math.random() * 16)];
  return s;
}
function mockB9AppendEvent(input: {
  destinationKey: string; snapshotId: string | null;
  kind: MockBkpEvent['kind']; message: string;
  detail?: unknown; actorId?: string;
}): MockBkpEvent {
  const ev: MockBkpEvent = {
    id: `mock-bevt-${mockB9.seq++}`,
    tenantId: 'default',
    ordinal: mockB9.events.length + 1,
    destinationKey: input.destinationKey,
    snapshotId: input.snapshotId,
    kind: input.kind, message: input.message,
    detail: input.detail ?? null,
    actorId: input.actorId ?? 'demo',
    createdAt: new Date().toISOString(),
  };
  mockB9.events.unshift(ev);
  return ev;
}

function handleB9MockChannel(channel: string, data: unknown): unknown | undefined {
  switch (channel) {
    case IPC.BACKUP_LIST_DESTINATIONS:
      return mockB9.destinations;
    case IPC.BACKUP_LIST_SCHEDULES:
      return mockB9.schedules;
    case IPC.BACKUP_UPSERT_SCHEDULE: {
      const args = data as {
        id?: string; destinationKey: string;
        cadence: MockSchedule['cadence']; enabled: boolean;
        retentionCount?: number; retentionAgeDays?: number;
      };
      const id = args.id ?? `mock-sched-${mockB9.seq++}`;
      const now = new Date().toISOString();
      const next = args.enabled
        ? new Date(Date.now() + CADENCE_MS_RENDERER[args.cadence]).toISOString()
        : null;
      const existing = mockB9.schedules.find((s) => s.id === id);
      const row: MockSchedule = existing ?? {
        id, tenantId: 'default',
        destinationKey: args.destinationKey,
        cadence: args.cadence,
        enabled: args.enabled,
        retentionCount:   args.retentionCount   ?? 10,
        retentionAgeDays: args.retentionAgeDays ?? 30,
        lastRunAt: null, nextRunAt: next,
        createdAt: now, updatedAt: now,
      };
      row.destinationKey   = args.destinationKey;
      row.cadence          = args.cadence;
      row.enabled          = args.enabled;
      row.retentionCount   = args.retentionCount   ?? row.retentionCount;
      row.retentionAgeDays = args.retentionAgeDays ?? row.retentionAgeDays;
      row.nextRunAt        = next;
      row.updatedAt        = now;
      if (!existing) mockB9.schedules.unshift(row);
      mockB9AppendEvent({
        destinationKey: row.destinationKey, snapshotId: null,
        kind: 'schedule_updated',
        message: `Schedule '${row.cadence}' on ${row.destinationKey} (${row.enabled ? 'enabled' : 'disabled'}).`,
      });
      return row;
    }
    case IPC.BACKUP_DELETE_SCHEDULE: {
      const { id } = data as { id: string };
      const idx = mockB9.schedules.findIndex((s) => s.id === id);
      if (idx >= 0) {
        const [removed] = mockB9.schedules.splice(idx, 1);
        mockB9AppendEvent({
          destinationKey: removed!.destinationKey, snapshotId: null,
          kind: 'schedule_deleted',
          message: `Schedule deleted (${removed!.cadence}).`,
        });
      }
      return { ok: true };
    }
    case IPC.BACKUP_RUN_MANUAL: {
      const args = data as { destinationKey: string; kind?: 'full' | 'incremental' };
      mockB9AppendEvent({
        destinationKey: args.destinationKey, snapshotId: null,
        kind: 'manual_started', message: 'Manual backup requested.',
      });
      const id = `mock-snap-${mockB9.seq++}`;
      const snap: MockSnapshot = {
        id, tenantId: 'default',
        destinationKey: args.destinationKey,
        kind: args.kind ?? 'full',
        sizeBytes: 12_582_912 + Math.floor(Math.random() * 4_194_304),
        sha256: mockB9Sha(),
        encryption: 'aes-256-gcm',
        storageRef: `/mock/backups/default/${id}.bbu`,
        verifiedAt: null, verifyError: null,
        schedulesId: null,
        createdBy: 'demo',
        createdAt: new Date().toISOString(),
      };
      mockB9.snapshots.unshift(snap);
      mockB9AppendEvent({
        destinationKey: args.destinationKey, snapshotId: id,
        kind: 'completed',
        message: `Snapshot completed (${snap.kind}, ${snap.sizeBytes} bytes).`,
        detail: { sha256: snap.sha256, kind: snap.kind },
      });
      return snap;
    }
    case IPC.BACKUP_LIST_SNAPSHOTS: {
      const args = data as { destinationKey?: string } | undefined;
      return args?.destinationKey
        ? mockB9.snapshots.filter((s) => s.destinationKey === args.destinationKey)
        : mockB9.snapshots;
    }
    case IPC.BACKUP_VERIFY_SNAPSHOT: {
      const { id } = data as { id: string };
      const snap = mockB9.snapshots.find((s) => s.id === id);
      if (!snap) throw new IpcCallError({
        code: 'B9_SNAPSHOT_NOT_FOUND', message: `Snapshot not found: ${id}`,
      });
      snap.verifiedAt = new Date().toISOString();
      snap.verifyError = null;
      mockB9AppendEvent({
        destinationKey: snap.destinationKey, snapshotId: id,
        kind: 'verified', message: 'Snapshot verified.',
      });
      return { ok: true };
    }
    case IPC.BACKUP_RESTORE_SNAPSHOT: {
      const { snapshotId, confirmation } = data as {
        snapshotId: string; confirmation: string;
      };
      if (confirmation !== 'CONFIRM_OVERWRITE') {
        throw new IpcCallError({ code: 'B9_RESTORE_UNCONFIRMED',
          message: 'Restore requires explicit operator confirmation.' });
      }
      const snap = mockB9.snapshots.find((s) => s.id === snapshotId);
      if (!snap) throw new IpcCallError({
        code: 'B9_SNAPSHOT_NOT_FOUND', message: `Snapshot not found: ${snapshotId}`,
      });
      if (!snap.verifiedAt) {
        throw new IpcCallError({ code: 'B9_VERIFY_FAILED',
          message: 'Backup verification failed: snapshot must be verified before restore' });
      }
      mockB9AppendEvent({
        destinationKey: snap.destinationKey, snapshotId,
        kind: 'restore_started', message: 'Restore started.',
      });
      mockB9AppendEvent({
        destinationKey: snap.destinationKey, snapshotId,
        kind: 'restore_completed',
        message: `Restore completed from snapshot ${snapshotId}.`,
        detail: { bytes: snap.sizeBytes },
      });
      return { ok: true, bytesWritten: snap.sizeBytes };
    }
    case IPC.BACKUP_LIST_EVENTS:
      return mockB9.events;
    case IPC.BACKUP_HEALTH: {
      const destinations = mockB9.destinations.map((d) => {
        const ours = mockB9.events.filter((e) => e.destinationKey === d.key);
        const lastSuccess = ours.find((e) => e.kind === 'completed');
        const lastFailure = ours.find((e) => e.kind === 'failed');
        const lastVerify  = ours.find((e) => e.kind === 'verified');
        const schedules   = mockB9.schedules.filter((s) =>
          s.destinationKey === d.key && s.enabled);
        const next = schedules.map((s) => s.nextRunAt).filter((x): x is string => !!x).sort()[0] ?? null;
        return {
          key: d.key, displayName: d.displayName, ready: true,
          lastSuccessAt:   lastSuccess?.createdAt ?? null,
          lastFailureAt:   lastFailure?.createdAt ?? null,
          lastVerifiedAt:  lastVerify?.createdAt  ?? null,
          snapshotCount:   mockB9.snapshots.filter((s) => s.destinationKey === d.key).length,
          nextScheduledAt: next,
        };
      });
      const overall: 'healthy' | 'warning' | 'unconfigured' =
        mockB9.schedules.length === 0 ? 'unconfigured'
        : destinations.every((d) => !d.lastFailureAt
              || (d.lastSuccessAt && d.lastSuccessAt > d.lastFailureAt))
          ? 'healthy' : 'warning';
      return { destinations, overall };
    }
    case IPC.BACKUP_SCHEDULER_STATUS:
      return {
        running: true,
        startedAt: new Date(Date.now() - 60_000).toISOString(),
        tickCount: Math.floor(performance.now() / 30_000),
        inFlight: [],
        lastTickAt: new Date().toISOString(),
        lastFireAt: mockB9.snapshots[0]?.createdAt ?? null,
        consecutiveFailures: {},
      };
    default: return undefined;
  }
}


// ---------------------------------------------------------------------------
// P1.4 — Health monitoring web-preview mock.
// ---------------------------------------------------------------------------
function buildHealthMock() {
  const checks = [
    { id: 'process.uptime',  label: 'Process uptime',
      level: 'healthy' as const,
      message: `up ${Math.floor(performance.now() / 1000)}s · RSS 312 MB · heap 96/156 MB`,
      detail: { uptimeSec: Math.floor(performance.now() / 1000),
        rssMb: 312, heapUsedMb: 96, heapTotalMb: 156,
        nodeVersion: 'v20.11.1', platform: 'web-preview', arch: 'x64' },
      durationMs: 1 },
    { id: 'process.lifecycle', label: 'Lifecycle (clean shutdown)',
      level: 'healthy' as const,
      message: 'Previous shutdown was clean.',
      detail: { cleanShutdown: true, integrityOk: null,
        detectedAt: new Date().toISOString(), auditedTenants: 0 },
      durationMs: 1 },
    { id: 'process.disk',    label: 'Disk free (user data)',
      level: 'healthy' as const,
      message: '42,184 MB free · 512,000 MB total',
      detail: { freeMb: 42_184, totalMb: 512_000,
        path: '/mock/userdata', minFreeMb: 512 },
      durationMs: 2 },
    { id: 'db.integrity',    label: 'Database integrity',
      level: 'healthy' as const,
      message: 'integrity ok · 14,328 KB',
      detail: { integrityCheck: 'ok', fileBytes: 14_672_384 },
      durationMs: 11 },
    { id: 'db.migrations',   label: 'Database migrations',
      level: 'healthy' as const,
      message: '7 migrations applied',
      detail: { applied: 7 }, durationMs: 1 },
    { id: 'backup.recent',   label: 'Recent backup',
      level: (mockB9.snapshots.length === 0 ? 'warn' : 'healthy') as 'warn' | 'healthy',
      message: mockB9.snapshots.length === 0
        ? 'No backup snapshots recorded.' : 'latest 0.1h ago · verified',
      detail: { snapshotCount: mockB9.snapshots.length,
        ageHours: mockB9.snapshots.length === 0 ? null : 0.1,
        verified: mockB9.snapshots.some((s) => s.verifiedAt !== null) },
      durationMs: 3 },
    { id: 'backup.schedule', label: 'Backup schedule',
      level: (mockB9.schedules.length === 0 ? 'warn' : 'healthy') as 'warn' | 'healthy',
      message: `${mockB9.schedules.filter((s) => s.enabled).length}/${mockB9.schedules.length} enabled · 0 missed`,
      detail: { total: mockB9.schedules.length,
        enabled: mockB9.schedules.filter((s) => s.enabled).length, missed: 0 },
      durationMs: 2 },
    { id: 'sync.queue',      label: 'Sync queue',
      level: 'healthy' as const,
      message: '0 pending',
      detail: { pending: 0, thresholds: { warn: 50, critical: 500 } },
      durationMs: 2 },
  ];
  const summary = { healthy: 0, warn: 0, critical: 0, unknown: 0 };
  for (const c of checks) summary[c.level as 'healthy' | 'warn' | 'critical' | 'unknown']++;
  const levels = checks.map((c) => c.level as string);
  const worst = levels.includes('critical') ? 'critical'
              : levels.includes('warn') || levels.includes('unknown') ? 'warn'
              : 'healthy';
  return { takenAt: new Date().toISOString(), overall: worst, checks, summary };
}

// ---------------------------------------------------------------------------
// P1.3 — Automatic Migrations + Safe Rollback web-preview mock.
// State persists for the page session so the "apply pending" demo flow is
// coherent (counters increment, history grows, snapshots accrue).
// ---------------------------------------------------------------------------
const mockMig: {
  history: Array<{ version: string; name: string; appliedAt: string }>;
  pending: Array<{ name: string; hash: string | null }>;
  snapshots: Array<{ path: string; sizeBytes: number; sha256: string;
    createdAt: string; reason: string }>;
  integrityOk: boolean | null;
  integrityCheckedAt: string | null;
  seq: number;
} = (() => {
  const now = new Date();
  const histTs = (n: number) => new Date(now.getTime() - n * 24 * 3600 * 1000).toISOString();
  return {
    history: [
      { version: 'h0',  name: '0000_initial',              appliedAt: histTs(120) },
      { version: 'h1',  name: '0001_sticky_vanisher',      appliedAt: histTs(95)  },
      { version: 'h2',  name: '0002_magenta_mad_thinker',  appliedAt: histTs(80)  },
      { version: 'h3',  name: '0003_young_lizard',         appliedAt: histTs(60)  },
      { version: 'h4',  name: '0004_fixed_justice',        appliedAt: histTs(45)  },
      { version: 'h5',  name: '0005_lethal_silver_samurai', appliedAt: histTs(30) },
      { version: 'h6',  name: '0006_normal_jazinda',       appliedAt: histTs(14)  },
      { version: 'h7',  name: '0007_quick_doctor_strange', appliedAt: histTs(1)   },
    ],
    pending: [],
    snapshots: [],
    integrityOk: true,
    integrityCheckedAt: now.toISOString(),
    seq: 0,
  };
})();

function handleMigrationsMockChannel(channel: string, _data: unknown): unknown | undefined {
  switch (channel) {
    case IPC.MIGRATIONS_STATUS:
      return {
        currentVersion:     mockMig.history[mockMig.history.length - 1]?.version ?? null,
        appliedCount:       mockMig.history.length,
        pendingCount:       mockMig.pending.length,
        pending:            mockMig.pending,
        history:            mockMig.history,
        lastAppliedAt:      mockMig.history[mockMig.history.length - 1]?.appliedAt ?? null,
        integrityOk:        mockMig.integrityOk,
        integrityCheckedAt: mockMig.integrityCheckedAt,
      };
    case IPC.MIGRATIONS_LIST_SNAPSHOTS:
      return mockMig.snapshots.slice();
    case IPC.MIGRATIONS_APPLY_PENDING: {
      if (mockMig.pending.length === 0) {
        return {
          noop: true, snapshotPath: null, snapshotSha: null, snapshotBytes: 0,
          applied: [], durationMs: 12, integrityOk: true,
          rolledBack: false, rollbackReason: null, error: null,
        };
      }
      const snapPath = `/mock/migration-snapshots/pre-${Date.now()}-${mockMig.seq++}.db`;
      const snap = {
        path: snapPath, sizeBytes: 196_608,
        sha256: Array.from({ length: 64 }, () => 'abcdef0123456789'[
          Math.floor(Math.random() * 16)]).join(''),
        createdAt: new Date().toISOString(),
        reason: `pre-migration:${mockMig.pending.map((p) => p.name).join(',')}`,
      };
      mockMig.snapshots.unshift(snap);
      if (mockMig.snapshots.length > 5) mockMig.snapshots.length = 5;
      const now = new Date().toISOString();
      const applied = mockMig.pending.map((p, i) => ({
        version: `v-mock-${mockMig.seq}-${i}`, name: p.name, appliedAt: now,
      }));
      mockMig.history = [...mockMig.history, ...applied];
      mockMig.pending = [];
      mockMig.integrityOk = true;
      mockMig.integrityCheckedAt = now;
      return {
        noop: false, snapshotPath: snapPath, snapshotSha: snap.sha256,
        snapshotBytes: snap.sizeBytes, applied, durationMs: 384,
        integrityOk: true, rolledBack: false, rollbackReason: null, error: null,
      };
    }
    case IPC.MIGRATIONS_RUN_SCORECARD: {
      const now = new Date().toISOString();
      mockMig.integrityOk = true;
      mockMig.integrityCheckedAt = now;
      return {
        status: {
          currentVersion:     mockMig.history[mockMig.history.length - 1]?.version ?? null,
          appliedCount:       mockMig.history.length,
          pendingCount:       mockMig.pending.length,
          pending:            mockMig.pending,
          history:            mockMig.history,
          lastAppliedAt:      mockMig.history[mockMig.history.length - 1]?.appliedAt ?? null,
          integrityOk:        true,
          integrityCheckedAt: now,
        },
        integrityOk:     true,
        integrityResult: 'ok',
        snapshotCount:   mockMig.snapshots.length,
        pendingCount:    mockMig.pending.length,
        pending:         mockMig.pending.map((p) => p.name),
        runAt:           now,
        durationMs:      8,
      };
    }
    default: return undefined;
  }
}

// ---------------------------------------------------------------------------
// P1.5 Logging + P1.6 Performance Profiling web-preview mocks.
// ---------------------------------------------------------------------------
const mockOps: {
  logs: Array<{ ts: string; level: string; message: string;
    service?: string; correlationId?: string; meta?: Record<string, unknown> }>;
  level: 'error' | 'warn' | 'info' | 'verbose' | 'debug';
} = {
  logs: [],
  level: 'info',
};

(function seedMockLogs() {
  const now = Date.now();
  const seed: Array<[number, string, string, string]> = [
    [60, 'info',  'BIM Platform bootstrap complete',           'Bootstrap'],
    [55, 'info',  'Migrations applied via DrizzleMigrationAdapter', 'Migrator'],
    [50, 'info',  'B9 Backup IPC handlers registered',         'B9BackupHandler'],
    [42, 'info',  'Migrations IPC handlers registered',        'MigrationsHandler'],
    [33, 'info',  'scheduler started',                         'BackupScheduler'],
    [27, 'info',  'span:scheduler.fire ok',                    'BackupScheduler'],
    [18, 'warn',  'scheduled backup advanced after retry',     'BackupScheduler'],
    [9,  'info',  'integrity scorecard complete',              'MigrationService'],
    [3,  'info',  'health probe completed',                    'HealthService'],
  ];
  for (const [secAgo, level, message, service] of seed) {
    mockOps.logs.push({
      ts: new Date(now - secAgo * 1000).toISOString(),
      level, message, service, meta: { service },
    });
  }
})();

function handleOpsMockChannel(channel: string, data: unknown): unknown | undefined {
  const args = (data ?? {}) as Record<string, unknown>;
  switch (channel) {
    case IPC.LOGS_RECENT: {
      const limit = typeof args['limit'] === 'number' ? args['limit'] : 200;
      return mockOps.logs.slice(-limit);
    }
    case IPC.LOGS_GET_LEVEL:
      return { level: mockOps.level };
    case IPC.LOGS_SET_LEVEL: {
      const lvl = String(args['level'] ?? 'info') as typeof mockOps.level;
      mockOps.level = lvl;
      mockOps.logs.push({
        ts: new Date().toISOString(),
        level: 'info', message: 'log level updated',
        service: 'LogsHandler', meta: { newLevel: lvl },
      });
      return { level: lvl };
    }
    case IPC.METRICS_SNAPSHOT: {
      // Synthetic but plausible baselines so the UI is informative.
      return {
        takenAt: new Date().toISOString(),
        histograms: [
          { name: 'ipc.backup:health',           count: 84, sumMs: 980,  minMs: 4,  maxMs: 28,  meanMs: 11, p50Ms: 10, p95Ms: 22, p99Ms: 27 },
          { name: 'ipc.backup:listSnapshots',    count: 84, sumMs: 760,  minMs: 3,  maxMs: 19,  meanMs: 9,  p50Ms: 8,  p95Ms: 16, p99Ms: 18 },
          { name: 'ipc.backup:schedulerStatus',  count: 504, sumMs: 1480, minMs: 1, maxMs: 7,   meanMs: 3,  p50Ms: 2,  p95Ms: 5,  p99Ms: 6  },
          { name: 'ipc.migrations:status',       count: 14, sumMs: 70,   minMs: 2,  maxMs: 9,   meanMs: 5,  p50Ms: 5,  p95Ms: 8,  p99Ms: 9  },
          { name: 'ipc.system:health',           count: 28, sumMs: 1140, minMs: 18, maxMs: 102, meanMs: 40, p50Ms: 36, p95Ms: 88, p99Ms: 100 },
          { name: 'scheduler.fire',              count: 4,  sumMs: 18840, minMs: 4200, maxMs: 5380, meanMs: 4710, p50Ms: 4690, p95Ms: 5380, p99Ms: 5380 },
        ],
        counters: [
          { name: 'ipc.backup:health.ok',        count: 84 },
          { name: 'ipc.backup:listSnapshots.ok', count: 84 },
          { name: 'ipc.backup:schedulerStatus.ok', count: 504 },
          { name: 'ipc.migrations:status.ok',    count: 14 },
          { name: 'ipc.system:health.ok',        count: 28 },
          { name: 'scheduler.fire.ok',           count: 4  },
        ],
      };
    }
    case IPC.METRICS_RESET:
      return { ok: true };
    default: return undefined;
  }
}

// ---------------------------------------------------------------------------
// B-FIELD.1 — Field Visit Workflow web-preview mock.
// ---------------------------------------------------------------------------
interface MockFieldSession {
  id: string; tenantId: string;
  parentObjectId: string;
  parentObjectType: 'project' | 'room' | 'rack' | 'device' | 'work_package';
  label: string;
  manifestHash: string;
  manifestBytes: number;
  encryptedBundleRef: string;
  status: 'generated' | 'in_progress' | 'completed' | 'imported' | 'expired';
  expiresAt: string | null;
  createdBy: string; createdAt: string;
  completedAt: string | null;
  importedAt: string | null;
}

type MockCaptureKind = 'note' | 'photo' | 'markup' | 'measurement' | 'scan' | 'voice' | 'issue';
type MockChecklistStatus = 'pending' | 'pass' | 'fail' | 'na';

interface MockFieldCapture {
  id: string; tenantId: string; sessionId: string;
  kind: MockCaptureKind;
  textBody: string | null;
  storageRef: string | null;
  sha256: string | null;
  bytes: number | null;
  mimeType: string | null;
  geoLat: number | null;
  geoLng: number | null;
  accuracyM: number | null;
  capturedAt: string;
  capturedBy: string;
  syncStatus: 'pending' | 'syncing' | 'synced' | 'failed';
  /** Mock-only: keeps the base64 payload so readCaptureBinary round-trips. */
  _payloadB64?: string;
}

interface MockChecklistItem {
  id: string; tenantId: string; sessionId: string;
  templateRef: string;
  title: string;
  status: MockChecklistStatus;
  evidenceCaptureId: string | null;
  notes: string | null;
  notedAt: string | null;
  notedBy: string | null;
  createdAt: string;
}

function randHex(n: number): string {
  let s = '';
  for (let i = 0; i < n; i++) s += '0123456789abcdef'[Math.floor(Math.random() * 16)];
  return s;
}
function randUuid(): string {
  // 8-4-4-4-12
  return `${randHex(8)}-${randHex(4)}-${randHex(4)}-${randHex(4)}-${randHex(12)}`;
}

const mockField: {
  sessions: MockFieldSession[];
  captures: MockFieldCapture[];
  checklist: MockChecklistItem[];
} = (() => {
  const now = Date.now();
  const seed = (n: number, label: string,
                parentType: MockFieldSession['parentObjectType'],
                parentId: string,
                status: MockFieldSession['status']): MockFieldSession => {
    const id = randUuid();
    return {
      id, tenantId: 'default',
      parentObjectId: parentId, parentObjectType: parentType,
      label,
      manifestHash: randHex(64),
      manifestBytes: 2048 + Math.floor(Math.random() * 4096),
      encryptedBundleRef: `default/${id.slice(0, 2)}/${id}.enc`,
      status,
      expiresAt: new Date(now + 30 * 86400000 - n * 3600000).toISOString(),
      createdBy: currentUserEmail(),
      createdAt: new Date(now - n * 3600000).toISOString(),
      completedAt: status === 'completed' ? new Date(now - n * 1800000).toISOString() : null,
      importedAt: status === 'imported' ? new Date(now - n * 600000).toISOString() : null,
    };
  };
  const sessions = [
    seed(2,  'Inspection 2026-02-15',      'project', 'PROJ-001', 'in_progress'),
    seed(8,  'Rack audit — IDF-A12',       'rack',    'RACK-A12', 'completed'),
    seed(36, 'Door room walkthrough',      'room',    'ROOM-303', 'generated'),
  ];
  // Seed a couple of captures + checklist items for the active session
  const active = sessions[0]!;
  const cap1: MockFieldCapture = {
    id: randUuid(), tenantId: 'default', sessionId: active.id,
    kind: 'note', textBody: 'Cable tray sagging near panel 14',
    storageRef: null, sha256: null, bytes: null, mimeType: null,
    geoLat: null, geoLng: null, accuracyM: null,
    capturedAt: new Date(now - 60 * 60 * 1000).toISOString(),
    capturedBy: currentUserEmail(), syncStatus: 'pending',
  };
  const cap2: MockFieldCapture = {
    id: randUuid(), tenantId: 'default', sessionId: active.id,
    kind: 'measurement', textBody: 'Patch panel to outlet: 28.4m',
    storageRef: null, sha256: null, bytes: null, mimeType: null,
    geoLat: null, geoLng: null, accuracyM: null,
    capturedAt: new Date(now - 50 * 60 * 1000).toISOString(),
    capturedBy: currentUserEmail(), syncStatus: 'pending',
  };
  const checklist: MockChecklistItem[] = [
    {
      id: randUuid(), tenantId: 'default', sessionId: active.id,
      templateRef: 'tia-568.outlet-1', title: 'Verify outlet labels match drawing',
      status: 'pending', evidenceCaptureId: null, notes: null,
      notedAt: null, notedBy: null,
      createdAt: new Date(now - 70 * 60 * 1000).toISOString(),
    },
    {
      id: randUuid(), tenantId: 'default', sessionId: active.id,
      templateRef: 'tia-568.outlet-2', title: 'Confirm cable management present',
      status: 'pass', evidenceCaptureId: null,
      notes: 'Verified on-site', notedAt: new Date(now - 30 * 60 * 1000).toISOString(),
      notedBy: currentUserEmail(),
      createdAt: new Date(now - 70 * 60 * 1000).toISOString(),
    },
  ];
  return { sessions, captures: [cap1, cap2], checklist };
})();

function handleFieldMockChannel(channel: string, data: unknown): unknown | undefined {
  const args = (data ?? {}) as Record<string, unknown>;
  switch (channel) {
    case IPC.FIELD_LIST_SESSIONS: {
      const tenant = String(args['tenantId'] ?? 'default');
      const lim = typeof args['limit'] === 'number' ? args['limit'] : 50;
      return mockField.sessions
        .filter((s) => s.tenantId === tenant)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .slice(0, lim);
    }
    case IPC.FIELD_LIST_FOR_PARENT: {
      const tenant = String(args['tenantId'] ?? 'default');
      const pt = String(args['parentObjectType']);
      const pid = String(args['parentObjectId']);
      return mockField.sessions
        .filter((s) => s.tenantId === tenant
                    && s.parentObjectType === pt && s.parentObjectId === pid)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }
    case IPC.FIELD_GET_SESSION: {
      const tenant = String(args['tenantId'] ?? 'default');
      const id = String(args['id']);
      const s = mockField.sessions.find((x) => x.tenantId === tenant && x.id === id);
      if (!s) throw new IpcCallError({ code: 'NOT_FOUND', message: `session ${id} not found` });
      return { session: s, qrPayload: `bim://field/${s.id}?h=${s.manifestHash.slice(0,16)}&t=${tenant}` };
    }
    case IPC.FIELD_CREATE_SESSION: {
      const id  = randUuid();
      const now = new Date().toISOString();
      const days = typeof args['expiresInDays'] === 'number' ? args['expiresInDays'] : 30;
      const tenant = String(args['tenantId'] ?? 'default');
      const session: MockFieldSession = {
        id, tenantId: tenant,
        parentObjectId:   String(args['parentObjectId']),
        parentObjectType: args['parentObjectType'] as MockFieldSession['parentObjectType'],
        label:            String(args['label']),
        manifestHash:     randHex(64),
        manifestBytes:    2048 + Math.floor(Math.random() * 4096),
        encryptedBundleRef: `${tenant}/${id.slice(0,2)}/${id}.enc`,
        status:           'generated',
        expiresAt:        new Date(Date.now() + days * 86400000).toISOString(),
        createdBy:        currentUserEmail(),
        createdAt:        now,
        completedAt:      null,
        importedAt:       null,
      };
      mockField.sessions.unshift(session);
      return {
        session,
        qrPayload: `bim://field/${session.id}?h=${session.manifestHash.slice(0,16)}&t=${tenant}`,
        manifest: {
          schemaVersion: 1,
          sessionId: session.id, tenantId: session.tenantId,
          parentObjectId: session.parentObjectId,
          parentObjectType: session.parentObjectType,
          label: session.label, generatedAt: now, generatedBy: session.createdBy,
          expiresAt: session.expiresAt,
          drawings: [], assets: [], checklists: [], previousNotes: [], openIssues: [],
        },
      };
    }
    case IPC.FIELD_READ_MANIFEST: {
      const tenant = String(args['tenantId'] ?? 'default');
      const id = String(args['id']);
      const s = mockField.sessions.find((x) => x.tenantId === tenant && x.id === id);
      if (!s) throw new IpcCallError({ code: 'NOT_FOUND', message: `session ${id} not found` });
      return {
        schemaVersion: 1,
        sessionId: s.id, tenantId: s.tenantId,
        parentObjectId: s.parentObjectId,
        parentObjectType: s.parentObjectType,
        label: s.label, generatedAt: s.createdAt, generatedBy: s.createdBy,
        expiresAt: s.expiresAt,
        drawings: [], assets: [], checklists: [], previousNotes: [], openIssues: [],
      };
    }
    case IPC.FIELD_UPDATE_STATUS: {
      const tenant = String(args['tenantId'] ?? 'default');
      const id = String(args['id']);
      const status = args['status'] as MockFieldSession['status'];
      const s = mockField.sessions.find((x) => x.tenantId === tenant && x.id === id);
      if (s) {
        s.status = status;
        if (status === 'completed') s.completedAt = new Date().toISOString();
        if (status === 'imported')  s.importedAt  = new Date().toISOString();
      }
      return { ok: true };
    }
    // -----------------------------------------------------------------------
    // B-FIELD.2 — capture / checklist channels
    // -----------------------------------------------------------------------
    case IPC.FIELD_LIST_CAPTURES: {
      const tenant = String(args['tenantId'] ?? 'default');
      const sid    = String(args['sessionId']);
      const kind   = args['kind'] as MockCaptureKind | undefined;
      return mockField.captures
        .filter((c) => c.tenantId === tenant && c.sessionId === sid && (!kind || c.kind === kind))
        .sort((a, b) => a.capturedAt.localeCompare(b.capturedAt))
        .map(stripPayload);
    }
    case IPC.FIELD_COUNT_CAPTURES: {
      const tenant = String(args['tenantId'] ?? 'default');
      const sid    = String(args['sessionId']);
      const byKind: Record<MockCaptureKind, number> = {
        note: 0, photo: 0, markup: 0, measurement: 0, scan: 0, voice: 0, issue: 0,
      };
      let total = 0;
      for (const c of mockField.captures) {
        if (c.tenantId === tenant && c.sessionId === sid) {
          byKind[c.kind]++; total++;
        }
      }
      return { total, byKind };
    }
    case IPC.FIELD_ADD_NOTE: {
      return addMockCapture({
        tenantId: String(args['tenantId'] ?? 'default'),
        sessionId: String(args['sessionId']),
        kind: 'note',
        textBody: String(args['text']),
        geoLat: numOrNull(args['geoLat']),
        geoLng: numOrNull(args['geoLng']),
        accuracyM: numOrNull(args['accuracyM']),
      });
    }
    case IPC.FIELD_ADD_MEASUREMENT: {
      return addMockCapture({
        tenantId: String(args['tenantId'] ?? 'default'),
        sessionId: String(args['sessionId']),
        kind: 'measurement',
        textBody: String(args['text']),
        geoLat: numOrNull(args['geoLat']),
        geoLng: numOrNull(args['geoLng']),
      });
    }
    case IPC.FIELD_ADD_SCAN: {
      return addMockCapture({
        tenantId: String(args['tenantId'] ?? 'default'),
        sessionId: String(args['sessionId']),
        kind: 'scan',
        textBody: String(args['code']),
      });
    }
    case IPC.FIELD_ADD_ISSUE: {
      const sev = (args['severity'] as 'low' | 'medium' | 'high' | undefined) ?? 'medium';
      return addMockCapture({
        tenantId: String(args['tenantId'] ?? 'default'),
        sessionId: String(args['sessionId']),
        kind: 'issue',
        textBody: JSON.stringify({ text: String(args['text']), severity: sev }),
      });
    }
    case IPC.FIELD_ADD_PHOTO: {
      const b64 = String(args['dataBase64']);
      const bytes = Math.floor(b64.length * 0.75);
      const captureId = randUuid();
      return addMockCapture({
        tenantId: String(args['tenantId'] ?? 'default'),
        sessionId: String(args['sessionId']),
        kind: 'photo',
        textBody: typeof args['caption'] === 'string' ? String(args['caption']) : null,
        storageRef: `default/${captureId.slice(0, 2)}/${captureId}.enc`,
        sha256: randHex(64),
        bytes,
        mimeType: String(args['mimeType']),
        geoLat: numOrNull(args['geoLat']),
        geoLng: numOrNull(args['geoLng']),
        accuracyM: numOrNull(args['accuracyM']),
        _payloadB64: b64,
        _id: captureId,
      });
    }
    case IPC.FIELD_READ_CAPTURE_BINARY: {
      const tenant = String(args['tenantId'] ?? 'default');
      const id = String(args['captureId']);
      const c = mockField.captures.find((x) => x.tenantId === tenant && x.id === id);
      if (!c) throw new IpcCallError({ code: 'NOT_FOUND', message: `capture ${id} not found` });
      const b64 = c._payloadB64 ?? '';
      return { base64: b64, bytes: Math.floor(b64.length * 0.75) };
    }
    case IPC.FIELD_DELETE_CAPTURE: {
      const tenant = String(args['tenantId'] ?? 'default');
      const id = String(args['captureId']);
      mockField.captures = mockField.captures.filter((c) => !(c.tenantId === tenant && c.id === id));
      return { ok: true };
    }
    case IPC.FIELD_CREATE_CHECKLIST_ITEM: {
      const item: MockChecklistItem = {
        id: randUuid(),
        tenantId: String(args['tenantId'] ?? 'default'),
        sessionId: String(args['sessionId']),
        templateRef: String(args['templateRef']),
        title: String(args['title']),
        status: 'pending',
        evidenceCaptureId: null,
        notes: null,
        notedAt: null, notedBy: null,
        createdAt: new Date().toISOString(),
      };
      mockField.checklist.push(item);
      return item;
    }
    case IPC.FIELD_CREATE_CHECKLIST_BATCH: {
      const tenant = String(args['tenantId'] ?? 'default');
      const sid    = String(args['sessionId']);
      const items  = (args['items'] as Array<{ templateRef: string; title: string }>).map((i) => ({
        id: randUuid(),
        tenantId: tenant, sessionId: sid,
        templateRef: i.templateRef, title: i.title,
        status: 'pending' as MockChecklistStatus,
        evidenceCaptureId: null, notes: null,
        notedAt: null, notedBy: null,
        createdAt: new Date().toISOString(),
      } satisfies MockChecklistItem));
      mockField.checklist.push(...items);
      return items;
    }
    case IPC.FIELD_LIST_CHECKLIST: {
      const tenant = String(args['tenantId'] ?? 'default');
      const sid = String(args['sessionId']);
      return mockField.checklist
        .filter((c) => c.tenantId === tenant && c.sessionId === sid)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    }
    case IPC.FIELD_SET_CHECKLIST_STATUS: {
      const tenant = String(args['tenantId'] ?? 'default');
      const id = String(args['itemId']);
      const status = args['status'] as MockChecklistStatus;
      const item = mockField.checklist.find((x) => x.tenantId === tenant && x.id === id);
      if (!item) throw new IpcCallError({ code: 'NOT_FOUND', message: `checklist item ${id} not found` });
      item.status = status;
      item.notedAt = new Date().toISOString();
      item.notedBy = currentUserEmail();
      if (args['evidenceCaptureId'] !== undefined) item.evidenceCaptureId = (args['evidenceCaptureId'] as string | null);
      if (args['notes'] !== undefined) item.notes = (args['notes'] as string | null);
      return item;
    }
    default: return undefined;
  }
}

function stripPayload(c: MockFieldCapture): Omit<MockFieldCapture, '_payloadB64'> {
  const { _payloadB64: _drop, ...rest } = c;
  void _drop;
  return rest;
}

function numOrNull(v: unknown): number | null {
  return typeof v === 'number' ? v : null;
}

function addMockCapture(input: Partial<MockFieldCapture> & {
  tenantId: string; sessionId: string; kind: MockCaptureKind;
  _id?: string;
}): MockFieldCapture {
  const s = mockField.sessions.find((x) => x.tenantId === input.tenantId && x.id === input.sessionId);
  if (!s) throw new IpcCallError({ code: 'NOT_FOUND', message: `session ${input.sessionId} not found` });
  if (s.status !== 'generated' && s.status !== 'in_progress') {
    throw new IpcCallError({ code: 'CONFLICT',
      message: `session ${input.sessionId} is not active for capture (status=${s.status})` });
  }
  const id = input._id ?? randUuid();
  const cap: MockFieldCapture = {
    id, tenantId: input.tenantId, sessionId: input.sessionId,
    kind: input.kind,
    textBody: input.textBody ?? null,
    storageRef: input.storageRef ?? null,
    sha256: input.sha256 ?? null,
    bytes: input.bytes ?? null,
    mimeType: input.mimeType ?? null,
    geoLat: input.geoLat ?? null,
    geoLng: input.geoLng ?? null,
    accuracyM: input.accuracyM ?? null,
    capturedAt: new Date().toISOString(),
    capturedBy: currentUserEmail(),
    syncStatus: 'pending',
    ...(input._payloadB64 ? { _payloadB64: input._payloadB64 } : {}),
  };
  mockField.captures.push(cap);
  return stripPayload(cap) as MockFieldCapture;
}

// ---------------------------------------------------------------------------
// GCPE.1 — Change Propagation web-preview mock.
// ---------------------------------------------------------------------------
type PropStatus = 'pending' | 'approved' | 'rejected' | 'propagating' | 'completed' | 'failed';
type EmStatus   = 'pending' | 'running' | 'success' | 'failed' | 'skipped';
interface MockChangeEvent {
  id: string; tenantId: string; projectId: string;
  entityType: string; entityId: string; field: string;
  oldValue: string | null; newValue: string | null;
  proposedBy: string; proposedAt: string;
  approvedBy: string | null; approvedAt: string | null;
  status: PropStatus; correlationId: string;
  prevHash: string | null; eventHash: string;
  reason: string | null;
}
interface MockEmission {
  id: string; eventId: string; tenantId: string; emitterKey: string;
  status: EmStatus;
  startedAt: string | null; finishedAt: string | null;
  artifactRef: string | null; errorMessage: string | null;
  createdAt: string;
}

const MOCK_EMITTERS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'doc.drawing.index',        label: 'Drawing index' },
  { key: 'doc.cover-sheet',          label: 'Cover sheet' },
  { key: 'doc.general-notes',        label: 'General notes' },
  { key: 'doc.symbol-legend',        label: 'Symbol legend' },
  { key: 'doc.keynote-legend',       label: 'Keynote legend' },
  { key: 'doc.abbreviations',        label: 'Abbreviations' },
  { key: 'doc.pathway-plan',         label: 'Floor plans — pathways' },
  { key: 'doc.device-plan',          label: 'Floor plans — devices' },
  { key: 'doc.enlarged-tr-plans',    label: 'Enlarged TR/MDF/IDF plans' },
  { key: 'doc.six-detail-room-sheet',label: 'Six-detail telecom-room sheet' },
  { key: 'doc.qaqc-report',          label: 'QA/QC report' },
  { key: 'doc.revision-history',     label: 'Revision history' },
  { key: 'doc.pack-and-go-manifest', label: 'Pack-and-Go manifest' },
  { key: 'aeng.rack-elevation',      label: 'Rack elevations' },
  { key: 'aeng.single-line-diagram', label: 'Single-line diagram' },
  { key: 'aeng.fiber-riser',         label: 'Fiber backbone riser' },
  { key: 'aeng.copper-riser',        label: 'Copper riser' },
  { key: 'aeng.grounding-riser',     label: 'Grounding riser' },
  { key: 'aeng.cable-schedule',      label: 'Cable schedule' },
  { key: 'aeng.patch-schedule',      label: 'Patch schedule' },
  { key: 'aeng.port-schedule',       label: 'Port schedule' },
  { key: 'aeng.device-schedule',     label: 'Device schedule' },
  { key: 'aeng.fiber-schedule',      label: 'Fiber schedule' },
  { key: 'proj.bom',                 label: 'Bill of materials' },
  { key: 'proj.specifications',      label: 'Specifications' },
];

const mockProp: { events: MockChangeEvent[]; emissions: MockEmission[] } = {
  events: [],
  emissions: [],
};

function sha256Hex(input: string): string {
  // Deterministic 64-hex from input — adequate for preview mock only.
  let h1 = 0x6a09e667, h2 = 0xbb67ae85, h3 = 0x3c6ef372, h4 = 0xa54ff53a;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    h1 = (h1 * 31 + c) >>> 0;
    h2 = (h2 ^ (c + (h1 << 5))) >>> 0;
    h3 = (h3 + (c * 17) ^ (h2 >>> 3)) >>> 0;
    h4 = (h4 ^ (h3 + (c << 7))) >>> 0;
  }
  const part = (n: number): string => n.toString(16).padStart(8, '0');
  return part(h1) + part(h2) + part(h3) + part(h4)
       + part(h1 ^ h3) + part(h2 ^ h4) + part(h1 + h4) + part(h2 + h3);
}

function handlePropagationMockChannel(channel: string, data: unknown): unknown | undefined {
  const args = (data ?? {}) as Record<string, unknown>;
  switch (channel) {
    case IPC.PROPAGATION_LIST_EMITTERS:
      return { qaqcGate: null, emitters: [...MOCK_EMITTERS] };
    case IPC.PROPAGATION_LIST_EVENTS: {
      const tenant = String(args['tenantId'] ?? 'default');
      const pid    = String(args['projectId']);
      return mockProp.events
        .filter((e) => e.tenantId === tenant && e.projectId === pid
                    && (args['status'] === undefined || e.status === args['status']))
        .sort((a, b) => b.proposedAt.localeCompare(a.proposedAt))
        .slice(0, typeof args['limit'] === 'number' ? args['limit'] : 50);
    }
    case IPC.PROPAGATION_LIST_EMISSIONS: {
      const tenant = String(args['tenantId'] ?? 'default');
      const eid    = String(args['eventId']);
      return mockProp.emissions
        .filter((e) => e.tenantId === tenant && e.eventId === eid)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    }
    case IPC.PROPAGATION_PROPOSE: {
      const tenant = String(args['tenantId'] ?? 'default');
      const pid    = String(args['projectId']);
      const id     = randUuid();
      const now    = new Date().toISOString();
      const prev = mockProp.events
        .filter((e) => e.tenantId === tenant && e.projectId === pid)
        .sort((a, b) => b.proposedAt.localeCompare(a.proposedAt))[0];
      const ev: MockChangeEvent = {
        id, tenantId: tenant, projectId: pid,
        entityType: String(args['entityType']),
        entityId:   String(args['entityId']),
        field:      String(args['field']),
        oldValue:   args['oldValue'] === undefined || args['oldValue'] === null ? null : String(args['oldValue']),
        newValue:   args['newValue'] === undefined || args['newValue'] === null ? null : String(args['newValue']),
        proposedBy: currentUserEmail(),
        proposedAt: now,
        approvedBy: null, approvedAt: null,
        status: 'pending',
        correlationId: (args['correlationId'] as string | undefined) ?? id,
        prevHash: prev?.eventHash ?? null,
        eventHash: sha256Hex((prev?.eventHash ?? '') + JSON.stringify(args)),
        reason: (args['reason'] as string | undefined) ?? null,
      };
      mockProp.events.unshift(ev);
      return ev;
    }
    case IPC.PROPAGATION_APPROVE: {
      const tenant = String(args['tenantId'] ?? 'default');
      const eid    = String(args['eventId']);
      const ev = mockProp.events.find((e) => e.tenantId === tenant && e.id === eid);
      if (!ev) throw new IpcCallError({ code: 'NOT_FOUND', message: `event ${eid} not found` });
      if (ev.status !== 'pending') {
        throw new IpcCallError({ code: 'CONFLICT', message: `event ${eid} is not pending` });
      }
      ev.status     = args['approve'] === true ? 'approved' : 'rejected';
      ev.approvedBy = currentUserEmail();
      ev.approvedAt = new Date().toISOString();
      if (typeof args['note'] === 'string') ev.reason = String(args['note']);
      return ev;
    }
    case IPC.PROPAGATION_RUN: {
      const tenant = String(args['tenantId'] ?? 'default');
      const eid    = String(args['eventId']);
      const ev = mockProp.events.find((e) => e.tenantId === tenant && e.id === eid);
      if (!ev) throw new IpcCallError({ code: 'NOT_FOUND', message: `event ${eid} not found` });
      if (ev.status !== 'approved') {
        throw new IpcCallError({ code: 'CONFLICT', message: `event ${eid} not approved` });
      }
      ev.status = 'propagating';
      const startedAt = new Date().toISOString();
      // ---- QAQC gate first ------------------------------------------------
      const gateOutcome = mockEvaluateQaqcGate(ev);
      const gateEmission: MockEmission = {
        id: randUuid(), eventId: ev.id, tenantId: tenant,
        emitterKey: 'qaqc.gate',
        status: gateOutcome.blocking ? 'failed' : 'success',
        startedAt, finishedAt: new Date(Date.now() + 20).toISOString(),
        artifactRef: null,
        errorMessage: gateOutcome.blocking
          ? `QAQC gate failed (errors=${gateOutcome.errorCount})`
          : null,
        createdAt: startedAt,
      };
      mockProp.emissions.push(gateEmission);
      if (gateOutcome.blocking) {
        ev.status = 'failed';
        return { event: ev, emissions: [gateEmission] };
      }
      // ---- Downstream emitters --------------------------------------------
      const ems: MockEmission[] = MOCK_EMITTERS.map((e) => ({
        id: randUuid(), eventId: ev.id, tenantId: tenant,
        emitterKey: e.key, status: 'success',
        startedAt, finishedAt: new Date(Date.now() + 50).toISOString(),
        artifactRef: null, errorMessage: null,
        createdAt: startedAt,
      }));
      mockProp.emissions.push(...ems);
      ev.status = 'completed';
      return { event: ev, emissions: [gateEmission, ...ems] };
    }
    case IPC.PROPAGATION_LIST_BY_CORRELATION: {
      const tenant = String(args['tenantId'] ?? 'default');
      const cid    = String(args['correlationId']);
      return mockProp.events
        .filter((e) => e.tenantId === tenant && e.correlationId === cid)
        .sort((a, b) => a.proposedAt.localeCompare(b.proposedAt));
    }
    case IPC.PROPAGATION_VERIFY_CHAIN:
      return { ok: true, corruptEventId: null };
    default: return undefined;
  }
}

// ---------------------------------------------------------------------------
// QAQC.1 — minimal web-preview mock (Phase 1).
// ---------------------------------------------------------------------------
interface MockOverride { tenantId: string; eventId: string;
                        acknowledgedBy: string; reason: string; }
const mockOverrides: MockOverride[] = [];

const MOCK_QAQC_RULES: ReadonlyArray<{ id: string; label: string; severity: 'error' | 'warning' }> = [
  { id: 'connectivity.endpoint-pair', label: 'Connection changes name a valid endpoint side', severity: 'error' },
  { id: 'connectivity.required-fields', label: 'Structural fields may not be empty',          severity: 'error' },
  { id: 'engineering.deletion-reason', label: 'Deletions require an audit reason',            severity: 'error' },
  { id: 'engineering.id-pattern',      label: 'Identifier values use the project ID alphabet', severity: 'error' },
  { id: 'engineering.no-op-change',    label: 'Change carries an observable delta',          severity: 'warning' },
  { id: 'engineering.rack-u-position', label: 'Rack U-position is a non-negative integer',   severity: 'error' },
];

function mockEvaluateQaqcGate(ev: MockChangeEvent): { blocking: boolean; errorCount: number } {
  const override = mockOverrides.find((o) => o.tenantId === ev.tenantId && o.eventId === ev.id);
  if (override) return { blocking: false, errorCount: 0 };
  let errors = 0;
  const structural = ['label', 'name', 'parentId', 'rackId', 'roomId',
                      'portId', 'cableId', 'deviceId', 'outletId'];
  if (structural.includes(ev.field)
      && (ev.newValue === null || ev.newValue.trim() === '')) errors++;
  const idFields = ['portId', 'cableId', 'deviceId', 'outletId', 'rackId', 'roomId', 'assetId'];
  if (idFields.includes(ev.field) && ev.newValue && !/^[A-Za-z0-9._:/-]+$/.test(ev.newValue)) errors++;
  if (ev.entityType === 'rack' && (ev.field === 'uPosition' || ev.field === 'startU')
      && ev.newValue !== null) {
    const n = Number(ev.newValue);
    if (!Number.isInteger(n) || n < 0) errors++;
  }
  const deleting = ev.newValue === null || ev.newValue === '';
  if (deleting && (!ev.reason || ev.reason.trim().length === 0)) errors++;
  return { blocking: errors > 0, errorCount: errors };
}

function handleQaqcMockChannel(channel: string, data: unknown): unknown | undefined {
  const args = (data ?? {}) as Record<string, unknown>;
  switch (channel) {
    case IPC.QAQC_LIST_RULES:
      return { rules: MOCK_QAQC_RULES };
    case IPC.QAQC_ACKNOWLEDGE: {
      const tenant = String(args['tenantId'] ?? 'default');
      const eid    = String(args['eventId']);
      const reason = String(args['reason'] ?? '').trim();
      if (!reason) {
        throw new IpcCallError({ code: 'INVALID', message: 'Acknowledgement reason must not be empty' });
      }
      const existing = mockOverrides.find((o) => o.tenantId === tenant && o.eventId === eid);
      if (existing) return { ok: true, alreadyAcknowledged: true };
      mockOverrides.push({ tenantId: tenant, eventId: eid,
                           acknowledgedBy: currentUserEmail(), reason });
      return { ok: true, alreadyAcknowledged: false };
    }
    case IPC.QAQC_RUN_REPORT: {
      const tenant = String(args['tenantId'] ?? 'default');
      const eid    = String(args['eventId']);
      const ev = mockProp.events.find((e) => e.tenantId === tenant && e.id === eid);
      if (!ev) throw new IpcCallError({ code: 'NOT_FOUND', message: `event ${eid} not found` });
      const gate = mockEvaluateQaqcGate(ev);
      return {
        report: {
          eventId: ev.id, tenantId: ev.tenantId,
          ranAt: new Date().toISOString(),
          ok: !gate.blocking,
          results: [],
          errorCount: gate.errorCount,
          warningCount: 0,
        },
        blocking: gate.blocking,
      };
    }
    default: return undefined;
  }
}

// ---------------------------------------------------------------------------
// Standalone Portable Application — web preview mock for system:portableInfo
// + system:resetWorkspace. These IPC channels are admin-gated in Electron but
// the renderer mock always returns success so design preview keeps working.
// ---------------------------------------------------------------------------
function handlePortableMockChannel(channel: string, _data: unknown): unknown | undefined {
  switch (channel) {
    case IPC.SYSTEM_PORTABLE_INFO:
      return {
        root: '<web-preview>/data',
        mode: 'dev-cwd',
        portable: true,
        subpaths: {
          db: '<web-preview>/data/db',
          logs: '<web-preview>/data/logs',
          cache: '<web-preview>/data/cache',
          backups: '<web-preview>/data/backups',
          attachments: '<web-preview>/data/attachments-enc',
          templates: '<web-preview>/data/templates',
          projects: '<web-preview>/data/projects',
        },
      };
    case IPC.SYSTEM_RESET_WORKSPACE:
      return {
        startedAt:  new Date().toISOString(),
        finishedAt: new Date().toISOString(),
        portableRoot: '<web-preview>/data',
        mode: 'dev-cwd',
        totalBytesFreed: 0,
        cleared: [
          { key: 'cache',             path: '<web-preview>/data/cache',    entries: 0, bytes: 0, cleared: true },
          { key: 'tmp',               path: '<web-preview>/data/tmp',      entries: 0, bytes: 0, cleared: true },
          { key: 'sessions',          path: '<web-preview>/data/sessions', entries: 0, bytes: 0, cleared: true },
          { key: 'migration-scratch', path: '<web-preview>/data/db/migration-snapshots', entries: 0, bytes: 0, cleared: true },
          { key: 'logs-rotated',      path: '<web-preview>/data/logs',     entries: 0, bytes: 0, cleared: true },
          { key: 'email-outbox-cache',path: '<web-preview>/data/email-outbox', entries: 0, bytes: 0, cleared: true },
          { key: 'msal-cache',        path: '<web-preview>/data/security', entries: 0, bytes: 0, cleared: true },
        ],
        preserved: [
          { key: 'db',          path: '<web-preview>/data/db' },
          { key: 'attachments', path: '<web-preview>/data/attachments-enc' },
          { key: 'backups',     path: '<web-preview>/data/backups' },
          { key: 'templates',   path: '<web-preview>/data/templates' },
          { key: 'config',      path: '<web-preview>/data/config' },
          { key: 'projects',    path: '<web-preview>/data/projects' },
        ],
      };
    default: return undefined;
  }
}


// ---------------------------------------------------------------------------
// SIE web-preview extractor — tiny stand-in for the deterministic backend
// engine so the Quick Ingest flow can demo without Electron. Same Detection
// shape as packages/main/src/application/sie/types.ts.
// ---------------------------------------------------------------------------
interface MockDet { id: string; kind: string; payload: Record<string, unknown>;
  confidence: number; source: string; span: { start: number; end: number };
  status: 'new'; target: null; extractor: 'rules-v1-web' }
function mockSieExtract(text: string): MockDet[] {
  const out: MockDet[] = [];
  const seen = new Set<string>();
  const push = (kind: string, payload: Record<string, unknown>, source: string, idx: number, conf: number) => {
    const key = `${kind}:${source.slice(0, 60)}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ id: `mock-det-${out.length}-${Math.random().toString(36).slice(2,8)}`,
               kind, payload, confidence: conf, source,
               span: { start: idx, end: idx + source.length },
               status: 'new', target: null, extractor: 'rules-v1-web' });
  };

  // ── Emails ──────────────────────────────────────────────────────────────────
  for (const m of text.matchAll(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g))
    push('email', { email: m[0] }, m[0], m.index ?? 0, 95);

  // ── Phone numbers ────────────────────────────────────────────────────────────
  for (const m of text.matchAll(/(?:\+?\d{1,3}[\s.-]?)?(?:\(\d{2,4}\)|\d{2,4})[\s.-]?\d{3,4}[\s.-]?\d{3,4}/g)) {
    const d = m[0].replace(/\D/g, ''); if (d.length < 7) continue;
    push('phone', { phone: m[0], normalized: d }, m[0], m.index ?? 0, 85);
  }

  // ── Project / job numbers ────────────────────────────────────────────────────
  for (const m of text.matchAll(/\b([A-Z]{2,6}[-_/]\d{3,6}(?:[-_/]\d{1,4})?)\b/g))
    push('project', { number: m[0] }, m[0], m.index ?? 0, 85);

  // ── Organizations ────────────────────────────────────────────────────────────
  for (const m of text.matchAll(/\b[A-Z][A-Za-z0-9 &.,'-]{2,50}(?:Inc|Ltd|LLC|Corp|LLP|LP|Co|Group|Engineering|Architecture|Consulting|Partners|Associates|Solutions|Services|Systems|International|Industries|Construction|Contractors|Builders|Developers|Properties|Management|Technologies|Labs|Studio|Agency|Institute)\b\.?/g))
    push('organization', { name: m[0].trim().replace(/[.,;]$/, '') }, m[0].trim(), m.index ?? 0, 82);

  // ── Addresses ────────────────────────────────────────────────────────────────
  for (const m of text.matchAll(/\d+\s+[A-Z][A-Za-z0-9 .,-]{4,50}(?:Street|St|Avenue|Ave|Boulevard|Blvd|Road|Rd|Drive|Dr|Lane|Ln|Way|Court|Ct|Place|Pl|Circle|Cir|Highway|Hwy|Parkway|Pkwy)\.?\b/gi))
    push('address', { raw: m[0].trim() }, m[0].trim(), m.index ?? 0, 78);

  // ── Person names — labeled fields ────────────────────────────────────────────
  for (const m of text.matchAll(/(?:^|\n)\s*(?:Name|Contact|From|To|Attn|Prepared by|Submitted by|Reviewed by|PM|Project Manager|Engineer|Inspector|Owner|Client|Contractor|Designer|Architect)\s*[:–]\s*([A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,3})/g)) {
    const name = m[1]?.trim();
    if (name) push('contact', { fullName: name }, name, m.index ?? 0, 80);
  }

  // ── Person names — standalone (FirstName LastName, both capitalized) ────────
  for (const m of text.matchAll(/\b([A-Z][a-z]{1,20})\s+([A-Z][a-z]{1,20})(?:\s+([A-Z][a-z]{1,20}))?\b/g)) {
    const name = m[0].trim();
    // Skip if it looks like a heading or company
    if (/LLC|Inc|Corp|Ltd|Engineering|Architecture|Street|Avenue|Blvd/.test(name)) continue;
    push('contact', { fullName: name }, name, m.index ?? 0, 65);
  }

  // ── Dates ────────────────────────────────────────────────────────────────────
  const dateRe = /\b(?:\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s+\d{1,2},?\s+\d{2,4}|\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s+\d{2,4})\b/gi;
  for (const m of text.matchAll(dateRe))
    push('date', { raw: m[0] }, m[0], m.index ?? 0, 88);

  // ── Action items ─────────────────────────────────────────────────────────────
  for (const line of text.split(/\r?\n/)) {
    const t = line.trim();
    if (!t || t.length < 5) continue;
    const actionRe = /^\s*(?:[-*•►]\s+|action\s*item\s*:?\s*|todo\s*:?\s*|action\s*:?\s*)/i;
    if (actionRe.test(t)) {
      const clean = t.replace(actionRe, '').trim();
      if (clean.length > 3) push('action_item', { description: clean }, t, text.indexOf(t), 78);
    }
    // Lines with deadline markers
    const deadlineRe = /(?:due|by|before|deadline|complete\s+by|deliver\s+by)\s+(?:end\s+of\s+)?(?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)|\d{1,2}[\/\-])/i;
    if (deadlineRe.test(t)) push('action_item', { description: t, hasDeadline: true }, t, text.indexOf(t), 82);
  }

  // ── URLs / websites ──────────────────────────────────────────────────────────
  for (const m of text.matchAll(/https?:\/\/[^\s<>"]{4,}/g))
    push('url', { href: m[0] }, m[0], m.index ?? 0, 92);

  // ── Fallback: if nothing detected, extract non-empty lines as notes ──────────
  if (out.length === 0) {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 15);
    for (const l of lines.slice(0, 6))
      push('note', { text: l }, l, text.indexOf(l), 60);
  }

  return out;
}

async function mockCall<T>(channel: string, _data: unknown): Promise<T> {
  await new Promise((r) => setTimeout(r, 120));
  // ---------------------------------------------------------------------------
  // Meeting Intelligence Engine (MIE) Phase 1 — web preview returns deterministic
  // empty/placeholder data so the UI renders without recording flows.
  // ---------------------------------------------------------------------------
  if (channel === IPC.MIE_GET_LATEST) return null as unknown as T;
  if (channel === IPC.MIE_LIST || channel === IPC.MIE_ACTION_LIST) return [] as unknown as T;
  if (channel === IPC.MIE_EXTRACT) {
    return {
      id: 'mock-mie', meetingId: 'mock', projectId: 'mock',
      generatedAt: Date.now(), extractor: 'rules-v1',
      noteCount: 0, summary: '0 decision(s) · 0 action(s) · 0 risk(s) · 0 question(s) · 0 next-topic(s)',
      entities: [],
    } as unknown as T;
  }
  if (channel === IPC.MIE_ACTION_UPDATE) return { ok: true, status: 'open' } as unknown as T;
  // ---------------------------------------------------------------------------
  // Document Intelligence Engine (DIE) Phase 1 — web preview returns empty
  // registers + zeroed summaries. The desktop build is the source of truth.
  // ---------------------------------------------------------------------------
  if (channel === IPC.DIE_LIST || channel === IPC.DIE_DUPLICATES
   || channel === IPC.DIE_ORPHANS || channel === IPC.DIE_WARNINGS
   || channel === IPC.DIE_LINKS_FOR_TARGET) return [] as unknown as T;
  if (channel === IPC.DIE_SUMMARY) {
    return { total: 0, orphans: 0, superseded: 0, byType: {}, byDiscipline: {}, bySource: {} } as unknown as T;
  }
  if (channel === IPC.DIE_RESCAN_PROJECT) return { scanned: 0, reclassified: 0 } as unknown as T;
  if (channel === IPC.DIE_GET) return null as unknown as T;
  if (channel === IPC.DIE_LINK) {
    return { id: 'mock-link', documentId: 'mock', targetKind: 'note', targetId: 'mock',
             linkedBy: 'mock', linkedAt: Date.now(), reason: null } as unknown as T;
  }
  if (channel === IPC.DIE_UNLINK) return { ok: true } as unknown as T;
  // ---------------------------------------------------------------------------
  // Calendar Engine — Phase 1. Web preview returns empty lists + disconnected
  // sync state so the Schedule page renders end-to-end without Electron.
  // ---------------------------------------------------------------------------
  if (channel === IPC.CAL_LIST || channel === IPC.CAL_UPCOMING
   || channel === IPC.CAL_TODAY || channel === IPC.CAL_OVERDUE
   || channel === IPC.CAL_CONFLICTS) {
    return [] as unknown as T;
  }
  if (channel === IPC.CAL_PROVIDER_STATE || channel === IPC.CAL_SYNC_DIAGNOSTICS) {
    return { providerName: 'microsoft365', authStatus: 'disconnected',
             account: null, lastFullSyncAt: null, lastError: null,
             pendingCount: 0, inFlightCount: 0, failedCount: 0, conflictCount: 0 } as unknown as T;
  }
  if (channel === IPC.CAL_PROVIDER_CONNECT) {
    return { providerName: 'microsoft365', authStatus: 'failed',
             account: null, lastFullSyncAt: null,
             lastError: 'SCAFFOLD_PENDING: live Microsoft Graph wiring is a follow-up implementation pass.',
             pendingCount: 0, inFlightCount: 0, failedCount: 0, conflictCount: 0 } as unknown as T;
  }
  if (channel === IPC.CAL_SYNC_NOW)   return { processed: 0, failed: 0 } as unknown as T;
  if (channel === IPC.CAL_CREATE) {
    const now = Date.now();
    return {
      id: `mock-${now}`, projectId: null, clientId: null, buildingId: null,
      assetId: null, meetingId: null, actionItemId: null,
      kind: 'meeting', title: 'Mock event', description: null, location: null,
      startAt: now, endAt: now + 3_600_000, allDay: false,
      recurrenceRule: null, reminderMinutes: null, attendees: [],
      status: 'confirmed', source: 'manual',
      providerName: null, providerEventId: null,
      syncStatus: 'local_only', lastSyncedAt: null, conflictReason: null,
      createdAt: now, updatedAt: now,
    } as unknown as T;
  }
  if (channel === IPC.CAL_UPDATE)            return null as unknown as T;
  if (channel === IPC.CAL_DELETE)            return { ok: true } as unknown as T;
  if (channel === IPC.CAL_GET)               return null as unknown as T;
  if (channel === IPC.CAL_PROVIDER_DISCONNECT)
    return { providerName: 'microsoft365', authStatus: 'disconnected',
             account: null, lastFullSyncAt: null, lastError: null,
             pendingCount: 0, inFlightCount: 0, failedCount: 0, conflictCount: 0 } as unknown as T;
  if (channel === IPC.CAL_RESOLVE_CONFLICT)  return null as unknown as T;

  // -------------------------------------------------------------------------
  // Smart Ingestion Engine — web-preview mock. We can run the deterministic
  // extractors in the renderer too (pure functions), so the demo flow shows
  // real-looking detections. Writes are simulated.
  // -------------------------------------------------------------------------
  if (channel === IPC.SIE_PARSE) {
    const { text = '', sourceKind = 'paste', targetProject = null } = (_data ?? {}) as {
      text?: string; sourceKind?: string; targetProject?: string | null;
    };
    const detections = mockSieExtract(text);
    return {
      id: `mock-sie-${Date.now()}`,
      sourceKind, sourceText: text, targetProject,
      detections, detectionCount: detections.length,
      status: 'draft', createdAt: Date.now(),
      appliedAt: null, appliedSummary: null,
    } as unknown as T;
  }
  if (channel === IPC.SIE_APPLY_DRAFT) {
    const { approvals = [] } = (_data ?? {}) as { approvals: Array<{ accept: boolean }> };
    const accepted = approvals.filter((a) => a.accept).length;
    return {
      organizationsCreated: Math.min(2, accepted),
      contactsCreated:      Math.max(0, Math.floor(accepted / 3)),
      contactsLinked:       Math.max(0, Math.floor(accepted / 3)),
      meetingsCreated:      0,
      notesCreated:         Math.max(0, accepted - 4),
      errors:               [],
    } as unknown as T;
  }
  if (channel === IPC.SIE_LIST_DRAFTS)   return [] as unknown as T;
  if (channel === IPC.SIE_GET_DRAFT)     return null as unknown as T;
  if (channel === IPC.SIE_DISCARD_DRAFT) return { ok: true } as unknown as T;
  if (channel === IPC.CONTACT_LIST_BY_PROJECT) return [] as unknown as T;
  if (channel === IPC.CONTACT_LIST)            return [] as unknown as T;
  if (channel === IPC.ORG_LIST)                return [] as unknown as T;

  // -------------------------------------------------------------------------
  // Project File Explorer — web-preview synthetic tree so the docked drawer
  // is testable without a real document_register population.
  // -------------------------------------------------------------------------
  if (channel === IPC.PFE_TREE) {
    const { projectId = 'PREVIEW' } = (_data ?? {}) as { projectId?: string };
    const sample = {
      type: 'dir', name: projectId, path: '', fileCount: 4,
      children: [
        { type: 'dir', name: 'Drawings', path: 'Drawings', fileCount: 2,
          children: [
            { type: 'file', name: 'Sheet-A1.pdf', path: 'Drawings/Sheet-A1.pdf', fileId: 'preview-pdf-1',
              docType: 'drawing', discipline: 'architectural', revision: 'A',
              version: 1, status: 'current', source: 'local', sizeBytes: 145_320, lastModified: Date.now() - 86_400_000 },
            { type: 'file', name: 'Sheet-E1.pdf', path: 'Drawings/Sheet-E1.pdf', fileId: 'preview-pdf-2',
              docType: 'drawing', discipline: 'electrical', revision: 'B',
              version: 2, status: 'current', source: 'local', sizeBytes: 211_044, lastModified: Date.now() - 43_200_000 },
          ],
        },
        { type: 'dir', name: 'Meeting Notes', path: 'Meeting Notes', fileCount: 1,
          children: [
            { type: 'file', name: 'kickoff.md', path: 'Meeting Notes/kickoff.md', fileId: 'preview-md-1',
              docType: 'meeting', discipline: null, revision: null,
              version: 1, status: 'current', source: 'local', sizeBytes: 1_240, lastModified: Date.now() - 7_200_000 },
          ],
        },
        { type: 'file', name: 'README.txt', path: 'README.txt', fileId: 'preview-txt-1',
          docType: 'other', discipline: null, revision: null,
          version: 1, status: 'current', source: 'local', sizeBytes: 480, lastModified: Date.now() - 3_600_000 },
      ],
    };
    return sample as unknown as T;
  }
  if (channel === IPC.PFE_NODE_META) {
    const { fileId = 'unknown' } = (_data ?? {}) as { fileId?: string };
    return {
      fileId, baseName: fileId, fileName: `${fileId}.preview`,
      relativePath: `preview/${fileId}`, checksum: 'sha256-mock-deadbeef00112233',
      version: 1, status: 'current', source: 'local',
      docType: 'other', discipline: null, sheetNumber: null, revision: null,
      sizeBytes: 1024, lastModified: Date.now(),
      isOrphan: false, registeredAt: Date.now() - 86_400_000,
      classifiedAt: Date.now() - 86_400_000,
      meetingId: null, projectId: 'PREVIEW',
    } as unknown as T;
  }
  if (channel === IPC.PFE_PREVIEW) {
    const { fileId = '' } = (_data ?? {}) as { fileId?: string };
    if (fileId.includes('md') || fileId.includes('txt')) {
      return { kind: 'text', mime: 'text/markdown', fileName: 'sample.md', sizeBytes: 64,
               text: '# Sample preview\n\nThis is the web preview mock for the PFE.\n\nIn the desktop app this would render the real file via the DocumentSource resolver.' } as unknown as T;
    }
    return { kind: 'unsupported', mime: 'application/octet-stream',
             fileName: 'preview', sizeBytes: 0,
             reason: 'inline preview disabled in web preview (desktop only)' } as unknown as T;
  }

  // Meeting Capture Engine — Electron-only feature. The web preview must
  // not pretend audio capture works; we surface a hard, structured error
  // so the UI can show the "Electron only" disabled pill on the Record
  // button instead of silently mocking a non-functional flow.
  if (channel === IPC.MEETING_START || channel === IPC.MEETING_STOP
   || channel === IPC.MEETING_SAVE_AUDIO || channel === IPC.MEETING_READ_AUDIO
   || channel === IPC.MEETING_ADD_NOTE   || channel === IPC.MEETING_DELETE
   || channel === IPC.MEETING_RENAME) {
    throw new IpcCallError({
      code: 'ELECTRON_ONLY',
      message: 'Meeting Capture (audio recording) is only available in the desktop app.',
    });
  }
  // Read-only meeting channels return empty arrays in the web preview so
  // the panel can still render without recordings.
  if (channel === IPC.MEETING_LIST || channel === IPC.MEETING_LIST_NOTES) {
    return [] as unknown as T;
  }
  if (channel === IPC.MEETING_GET) {
    throw new IpcCallError({
      code: 'ELECTRON_ONLY',
      message: 'Meeting Capture is only available in the desktop app.',
    });
  }
  // Meeting Package Build (Cut C) — web preview returns a synthetic result so
  // the UI can render the success block without writing real files.
  if (channel === IPC.MEETING_PACKAGE_BUILD) {
    const args = (_data ?? {}) as { meetingId?: string };
    const id = args.meetingId ?? 'mock-meeting';
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const dir = `<web-preview>/projects/PREVIEW/meetings/packages/${id}-${ts}`;
    return {
      packageDir:             dir,
      pdfPath:                `${dir}/Meeting Report.pdf`,
      audioPath:              `${dir}/Meeting Audio.webm`,
      notesJsonPath:          `${dir}/Meeting Notes.json`,
      intelligenceJsonPath:   `${dir}/Meeting Intelligence.json`,
      actionRegisterJsonPath: `${dir}/Action Register.json`,
    } as T;
  }
  // Helper to dispatch B9 backup mock channel handlers separately.
  const b9 = handleB9MockChannel(channel, _data);
  if (b9 !== undefined) return b9 as T;
  // Helper to dispatch B7 locale mock channel handlers separately.
  const b7 = handleB7MockChannel(channel, _data);
  if (b7 !== undefined) return b7 as T;
  // FRIS — Feature Readiness Indicator System (web preview)
  if (channel === IPC.FRIS_STATUS)    return buildFrisMock() as T;
  if (channel === IPC.FRIS_RECOMPUTE) {
    const args = (_data ?? {}) as { id?: string };
    const all  = buildFrisMock();
    return (all.find((f) => f.id === args.id) ?? null) as T;
  }
  // P1.4 Health Monitoring
  if (channel === IPC.SYSTEM_HEALTH) return buildHealthMock() as T;
  // Standalone Portable Application — system:portableInfo + system:resetWorkspace
  const portable = handlePortableMockChannel(channel, _data);
  if (portable !== undefined) return portable as T;
  // Boot-time IPC integrity probe — in web preview we report everything OK.
  if (channel === IPC.SYSTEM_BOOT_PROBE) {
    const args = (_data ?? {}) as { channels?: string[] };
    const checked = (args.channels ?? []).map((c) => ({ channel: c, registered: true }));
    return { checkedAt: new Date().toISOString(), checked, missing: [] } as T;
  }
  // Project create — web preview echoes the DTO with a synthesised id.
  if (channel === IPC.PROJECT_CREATE) {
    const dto = ((_data ?? {}) as { dto?: Record<string, unknown> }).dto ?? {};
    const row = {
      id:            `proj-${Math.random().toString(36).slice(2, 10)}`,
      projectNumber: String(dto['projectNumber'] ?? 'NEW-001'),
      name:          String(dto['name']          ?? 'Untitled'),
      client:        String(dto['client']        ?? 'Unknown'),
      clientAddress: dto['clientAddress'] ? String(dto['clientAddress']) : null,
      phase:         String(dto['phase']         ?? 'planning'),
      status:        String(dto['status']        ?? 'active'),
      color:         dto['color'] ? String(dto['color']) : undefined,
    };
    __projectsMock.push(row);
    return {
      ...row,
      folderManifest: {
        projectId:      `proj-mock`,
        projectNumber:  row.projectNumber,
        layout: {
          root:        `<web-preview>/projects/${row.projectNumber} ${row.name}`,
          docs:        `<web-preview>/projects/.../docs`,
          revit:       `<web-preview>/projects/.../revit`,
          exports:     `<web-preview>/projects/.../exports`,
          cache:       `<web-preview>/projects/.../cache`,
          logs:        `<web-preview>/projects/.../logs`,
          projectJson: `<web-preview>/projects/.../project.json`,
        },
        createdAt: new Date().toISOString(),
        createdEntries: [],
        preExisted: false,
      },
    } as T;
  }
  if (channel === IPC.PROJECT_GET) {
    const id = ((_data ?? {}) as { id?: string }).id ?? 'p1';
    return {
      id, projectNumber: 'CAL-001', name: 'Calgary HQ — Telecom',
      client: 'RCD2 Inc.', phase: 'design', status: 'active',
    } as T;
  }
  // §4 Project lifecycle — all return ok with the new state stamp
  if (channel === IPC.PROJECT_ARCHIVE_TILE) return { ok: true, lifecycle: 'ARCHIVED' } as T;
  if (channel === IPC.PROJECT_RESTORE)      return { ok: true, lifecycle: 'ACTIVE'   } as T;
  if (channel === IPC.PROJECT_SOFT_DELETE)  return { ok: true, lifecycle: 'DELETED'  } as T;
  if (channel === IPC.PROJECT_PURGE)        return { removedFolderEntries: 0 } as T;
  // §7 Cloud — every channel returns a clear NOT_CONFIGURED / disabled response.
  if (channel === IPC.CLOUD_REMOTE_ACCESS_STATUS) {
    return { enabled: false, endpoint: null,
             message: 'Emergent-hosted remote access is not enabled in this build.' } as T;
  }
  if (channel === IPC.CLOUD_GDRIVE_STATUS) {
    return { code: 'NOT_CONFIGURED', account: null, lastSyncAt: null,
             message: 'Google Drive integration is not yet configured.' } as T;
  }
  if (channel === IPC.CLOUD_GDRIVE_OAUTH_BEGIN) {
    return { code: 'NOT_CONFIGURED', authUrl: null,
             message: 'OAuth client credentials are missing.' } as T;
  }
  if (channel === IPC.CLOUD_GDRIVE_SYNC) {
    const now = new Date().toISOString();
    return { code: 'NOT_CONFIGURED', synced: 0, conflicts: 0, errors: [],
             startedAt: now, finishedAt: now,
             message: 'Sync skipped — Google Drive is not configured.' } as T;
  }
  // Document Control Engine — synthesised sample registry for web preview
  if (channel === IPC.DOCREG_LIST) {
    const pid = ((_data ?? {}) as { projectId?: string }).projectId ?? 'p1';
    const now = new Date().toISOString();
    return { items: [
      { documentId: 'doc-a-2', projectId: pid, logicalKey: 'blueprint.pdf',
        filePath: `projects/${pid}/documents/current/blueprint.pdf`,
        fileHash: '7a3f…d2', fileSize: 1024 * 220, mimeType: 'application/pdf',
        revision: 2, status: 'ACTIVE', supersededBy: null,
        createdAt: now, updatedAt: now },
      { documentId: 'doc-a-1', projectId: pid, logicalKey: 'blueprint.pdf',
        filePath: `projects/${pid}/documents/superseded/r1-doc-a-1-blueprint.pdf`,
        fileHash: 'aa11…cc', fileSize: 1024 * 180, mimeType: 'application/pdf',
        revision: 1, status: 'SUPERSEDED', supersededBy: 'doc-a-2',
        createdAt: now, updatedAt: now },
      { documentId: 'doc-b-1', projectId: pid, logicalKey: 'site-plan.dwg',
        filePath: `projects/${pid}/documents/current/site-plan.dwg`,
        fileHash: 'cafe…22', fileSize: 1024 * 540, mimeType: 'application/dwg',
        revision: 1, status: 'ACTIVE', supersededBy: null,
        createdAt: now, updatedAt: now },
    ] } as T;
  }
  if (channel === IPC.DOCREG_REVISIONS) {
    const args = ((_data ?? {}) as { projectId?: string; logicalKey?: string });
    const pid = args.projectId ?? 'p1';
    const now = new Date().toISOString();
    return { items: [
      { documentId: 'doc-a-2', projectId: pid, logicalKey: args.logicalKey ?? 'blueprint.pdf',
        filePath: `projects/${pid}/documents/current/${args.logicalKey ?? 'blueprint.pdf'}`,
        fileHash: '7a3f…d2', fileSize: 1024 * 220, mimeType: null,
        revision: 2, status: 'ACTIVE', supersededBy: null,
        createdAt: now, updatedAt: now },
      { documentId: 'doc-a-1', projectId: pid, logicalKey: args.logicalKey ?? 'blueprint.pdf',
        filePath: `projects/${pid}/documents/superseded/r1-doc-a-1-${args.logicalKey ?? 'blueprint.pdf'}`,
        fileHash: 'aa11…cc', fileSize: 1024 * 180, mimeType: null,
        revision: 1, status: 'SUPERSEDED', supersededBy: 'doc-a-2',
        createdAt: now, updatedAt: now },
    ] } as T;
  }
  if (channel === IPC.DOCREG_LAYOUT) {
    const pid = ((_data ?? {}) as { projectId?: string }).projectId ?? 'p1';
    const root = `<web-preview>/projects/${pid}/documents`;
    return {
      root,
      current:    `${root}/current`,
      revisions:  `${root}/revisions`,
      superseded: `${root}/superseded`,
      metadata:   `${root}/metadata`,
    } as T;
  }
  if (channel === IPC.DOCREG_INGEST) {
    return { kind: 'first-revision', row: {
      documentId: 'doc-mock', projectId: 'p1', logicalKey: 'mock.txt',
      filePath: 'projects/p1/documents/current/mock.txt',
      fileHash: 'mock-hash', fileSize: 12, mimeType: 'text/plain',
      revision: 1, status: 'ACTIVE', supersededBy: null,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    } } as T;
  }
  if (channel === IPC.DOCREG_GET) {
    return null as T;
  }
  if (channel === IPC.DOCREG_EXPORT_SNAPSHOT) {
    return {
      path: '<web-preview>/snapshots/p1/2026-02-26.json',
      relativePath: 'snapshots/p1/2026-02-26.json',
      bytes: 4096,
    } as T;
  }
  if (channel === IPC.INSTANCE_GET) {
    return {
      instanceId:    '00000000-0000-0000-0000-000000000000',
      workspaceRoot: '<web-preview>/data',
      createdAt:     new Date().toISOString(),
      gmail:         null,
      version:       1,
    } as T;
  }
  if (channel === IPC.INSTANCE_SET_GMAIL) {
    const args = (_data ?? {}) as { gmail?: string };
    return {
      instanceId:    '00000000-0000-0000-0000-000000000000',
      workspaceRoot: '<web-preview>/data',
      createdAt:     new Date().toISOString(),
      gmail:         args.gmail ?? null,
      version:       1,
    } as T;
  }
  // P1.3 Automatic Migrations + Safe Rollback
  const mig = handleMigrationsMockChannel(channel, _data);
  if (mig !== undefined) return mig as T;
  // P1.5 Logging + P1.6 Performance Profiling
  const ops = handleOpsMockChannel(channel, _data);
  if (ops !== undefined) return ops as T;
  // B-FIELD.1 — Field Visit Workflow
  const field = handleFieldMockChannel(channel, _data);
  if (field !== undefined) return field as T;
  // GCPE.1 — Change Propagation
  const prop = handlePropagationMockChannel(channel, _data);
  if (prop !== undefined) return prop as T;
  // QAQC.1 — Validation gate
  const qaqc = handleQaqcMockChannel(channel, _data);
  if (qaqc !== undefined) return qaqc as T;
  switch (channel) {
    case IPC.AUTH_ENTRA_STATUS: return { enabled: false } as T;
    case IPC.AUTH_GET_SESSION:
    case IPC.AUTH_LOGIN:
      throw new IpcCallError({ code: 'PRODUCTION_AUTH_REQUIRED', message: 'Production authentication must use /api/auth endpoints.' });
    case IPC.SYNC_STATUS: return { running: false, lastRunAt: new Date().toISOString(), lastSummary: { processed: 14, succeeded: 12, failed: 0, conflicts: 2, durationMs: 238 }, pendingCount: 14 } as T;
    case IPC.BUILDING_LIST: return { items: [], total: 0, page: 1, pageSize: 20, totalPages: 0 } as T;
    case IPC.PROJECT_LIST: {
      const demoRows: MockProjectRow[] = isDemoMode()
        ? [{
            id:            'demo-sierra-manor',
            projectNumber: 'SM-2026-001',
            name:          SIERRA_MANOR_PROJECT_NAME,
            client:        'Sierra Manor Development Group',
            clientAddress: '1400 Ridgeline Blvd, Calgary, AB',
            phase:         'construction',
            status:        'active',
          }]
        : [];
      const items = [...demoRows, ...__projectsMock];
      return {
        items,
        total: items.length,
        page: 1, pageSize: 20,
        totalPages: Math.max(1, Math.ceil(items.length / 20)),
      } as T;
    }
    case IPC.ASSET_LIST: return { items: [], total: 0, page: 1, pageSize: 20, totalPages: 0 } as T;
    case IPC.AI_LIST_PROVIDERS: return [
      { key: 'openai',    displayName: 'OpenAI',    models: ['gpt-5.5','gpt-5.4','gpt-5.4-mini'], streaming: true, ready: true },
      { key: 'anthropic', displayName: 'Anthropic', models: ['claude-sonnet-4-6','claude-opus-4-7'], streaming: true, ready: true },
    ] as T;
    case IPC.AI_GET_DEFAULT_PROVIDER: return { key: 'openai' } as T;
    case IPC.AI_SET_DEFAULT_PROVIDER: return { ok: true, key: 'openai' } as T;
    case IPC.PILOT_DEMO_STATUS: return { canReset: true, lastResetAt: null } as T;
    case IPC.CABLING_LIST_OUTLETS: {
      return [] as unknown as T;
    }
    case IPC.CABLING_LIST_PORTS_BY_PARENT: {
      return [] as unknown as T;
    }
    case IPC.CABLING_TRACE: {
      const args = _data as { portId: string };
      return {
        startPortId: args.portId,
        hops: [],
        terminatedAt: 'open',
      } as T;
    }
    case IPC.CABLING_CONNECT: {
      return { ok: false, error: 'No devices available in preview.' } as unknown as T;
    }
    case IPC.CABLING_DISCONNECT: return { ok: true } as T;
    case IPC.REVIT_BRIDGE_STATUS: return { port: 47821, listening: false, activeTokens: 0 } as T;
    case IPC.REVIT_BRIDGE_MINT_PAIR_CODE: return { code: '------', expiresAt: new Date().toISOString() } as T;
    case IPC.REVIT_BRIDGE_LIST_TOKENS: return [] as unknown as T;
    case IPC.REVIT_BRIDGE_REVOKE_TOKEN: return { ok: true } as T;
    case IPC.CABLING_FIND_FREE_PORT: {
      return null as unknown as T;
    }
    case IPC.CABLING_LIST_PARENTS: {
      return [] as unknown as T;
    }
    case IPC.DASHBOARD_STATS: return (
      isDemoMode()
        ? { ...SIERRA_MANOR_STATS, generatedAt: new Date().toISOString() }
        : {
            buildings: 0, floors: 0, rooms: 0, telecomSpaces: 0,
            racks: 0, assets: 0, switches: 0, patchPanels: 0,
            ports: 0, outlets: 0, connections: 0,
            fiberCables: 0, copperCables: 0, projects: 0, documents: 0,
            openClashes: 0, generatedAt: new Date().toISOString(),
          }
    ) as T;
    case IPC.DASHBOARD_RECENT_ACTIVITY:
      return (isDemoMode() ? sierraManorActivity() : []) as unknown as T;
    case IPC.PING_MAIN_PROCESS: return {
      ok: true, at: new Date().toISOString(), pid: 0,
    } as unknown as T;
    case IPC.PROJECT_LOG_ADD: {
      const args = _data as { projectId: string; message: string; author?: string };
      const entry = {
        id: `log-${Date.now()}-${Math.floor(Math.random() * 1e4)}`,
        projectId: args.projectId, timestamp: Date.now(),
        author: args.author ?? currentUserEmail(),
        message: args.message,
      };
      __projectLogsMock[args.projectId] = [...(__projectLogsMock[args.projectId] ?? []), entry];
      return entry as unknown as T;
    }
    case IPC.PROJECT_LOG_LIST: {
      const args = _data as { projectId: string; limit?: number };
      const rows = (__projectLogsMock[args.projectId] ?? []).slice()
        .sort((a, b) => b.timestamp - a.timestamp);
      return rows.slice(0, args.limit ?? 500) as unknown as T;
    }
    case IPC.PROJECT_LOG_DELETE: {
      const args = _data as { projectId: string; id: string };
      __projectLogsMock[args.projectId] = (__projectLogsMock[args.projectId] ?? [])
        .filter((e) => e.id !== args.id);
      return { ok: true } as unknown as T;
    }
    case IPC.PROJECT_LOG_CLEAR: {
      const args = _data as { projectId: string };
      const cleared = (__projectLogsMock[args.projectId] ?? []).length;
      __projectLogsMock[args.projectId] = [];
      return { ok: true, cleared } as unknown as T;
    }
    case IPC.PROJECT_LOG_SUMMARY: {
      const args = _data as { projectId: string; limit?: number };
      const rows = (__projectLogsMock[args.projectId] ?? []).slice()
        .sort((a, b) => a.timestamp - b.timestamp);
      const last = rows.slice(-(args.limit ?? 20));
      const lines = last.map((e) => {
        const d = new Date(e.timestamp);
        const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        return `- ${hhmm} ${e.message}`;
      });
      return {
        summary: ['Project Summary', ...lines].join('\n'),
        entryCount: last.length,
        generatedAt: new Date().toISOString(),
      } as unknown as T;
    }
    case IPC.FOLDER_MONITOR_STATUS: {
      const s = ensureFolderMonitorMock();
      return { ...s } as unknown as T;
    }
    case IPC.FOLDER_MONITOR_LIST_EVENTS: {
      const s = ensureFolderMonitorMock();
      const limit = (_data as { limit?: number } | undefined)?.limit ?? 100;
      return s.events.slice(0, limit) as unknown as T;
    }
    case IPC.FOLDER_MONITOR_SELECT: {
      const s = ensureFolderMonitorMock();
      try {
        const w = window as unknown as {
          showDirectoryPicker?: (opts?: { mode?: 'read' | 'readwrite' }) => Promise<{ name: string }>;
        };
        if (typeof w.showDirectoryPicker === 'function') {
          const handle = await w.showDirectoryPicker({ mode: 'read' });
          s.folder = handle.name;
          s.accessible = true;
          s.accessError = null;
          return { ok: true, folder: s.folder } as unknown as T;
        }
        return { ok: false, error: 'Folder picker is not supported in this browser. Use the desktop build.' } as unknown as T;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (/abort|cancel/i.test(msg)) return { ok: true, canceled: true } as unknown as T;
        return { ok: false, error: msg } as unknown as T;
      }
    }
    case IPC.FOLDER_MONITOR_START: {
      const s = ensureFolderMonitorMock();
      if (!s.folder) return { ok: false, error: 'No folder selected (mock).' } as unknown as T;
      s.active = true;
      s.startedAt = new Date().toISOString();
      startFolderMonitorMockTimer();
      return { ok: true, folder: s.folder } as unknown as T;
    }
    case IPC.FOLDER_MONITOR_STOP: {
      const s = ensureFolderMonitorMock();
      s.active = false;
      stopFolderMonitorMockTimer();
      return { ok: true } as unknown as T;
    }
    case IPC.FOLDER_MONITOR_CLEAR_EVENTS: {
      const s = ensureFolderMonitorMock();
      s.events = [];
      s.eventCount = 0;
      s.lastEvent = null;
      return { ok: true } as unknown as T;
    }
    case IPC.FOLDER_MONITOR_OPEN_IN_FS: {
      const s = ensureFolderMonitorMock();
      if (!s.folder) return { ok: false, error: 'No folder selected.' } as unknown as T;
      return { ok: true, folder: s.folder } as unknown as T;
    }
    case IPC.FOLDER_MONITOR_RUN_DIAGNOSTIC: {
      const s = ensureFolderMonitorMock();
      if (!s.folder) return { ok: false, error: 'No folder selected.' } as unknown as T;
      if (!s.active) return { ok: false, error: 'Monitor is not running.' } as unknown as T;
      // Simulate a synthetic created/deleted pair like the real diagnostic.
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const testPath = `${s.folder}/.bim-ops-monitor-test-${stamp}.txt`;
      const evt: MockFolderEvent = {
        id: `diag-${Date.now()}`, kind: 'created', relPath: `.bim-ops-monitor-test-${stamp}.txt`,
        path: testPath, sizeBytes: 64, mtimeMs: Date.now(), at: new Date().toISOString(),
      };
      s.events = [evt, ...s.events].slice(0, 100);
      s.eventCount += 1; s.lastEvent = evt;
      return { ok: true, testPath, eventSeen: true, durationMs: 42 } as unknown as T;
    }
    case IPC.COACH_SUMMARISE: {
      const args = _data as { projectId: string; question?: string; citationLimit?: number };
      const limit = Math.max(1, Math.min(5, args.citationLimit ?? 3));
      return {
        summaryVersion: 1,
        projectId:     args.projectId,
        phase:         'design',
        completionPercent: 30,
        status:        'attention_needed',
        headline:      'Project at 30% in design; 4 open task(s), 2 overdue, 0 info gap(s).',
        openTaskCount: 4,
        blockedTaskIds: ['T-101'],
        overdueTaskIds: ['T-101', 'T-102'],
        blockers: [
          { kind: 'task', ref: 'T-101', detail: 'task T-101 is overdue (due 2026-03-10)' },
          { kind: 'task', ref: 'T-102', detail: 'task T-102 is overdue (due 2026-03-12)' },
        ],
        recommendedFocus: 'Address overdue work item T-101 to unblock design progress.',
        citations: [
          { kind: 'document', id: 'doc-1', score: 0.86, snippet: '…validate rack elevations, verify cabling routes against pathway diagrams…', source: { ref: 'design-review-checklist.md' } },
          { kind: 'document', id: 'doc-2', score: 0.71, snippet: '…RFI register tracks open requests for information that block design freeze…', source: { ref: 'rfi-register.md' } },
          { kind: 'document', id: 'doc-3', score: 0.62, snippet: args.question ?? '…construction permit package: drawings, structural calcs…', source: { ref: 'permit-package.md' } },
        ].slice(0, limit),
        generatedAt: new Date().toISOString(),
      } as T;
    }
    case IPC.COACH_SCORECARD: {
      const args = _data as { baseline: { projects?: Array<{ projectId: string; expectedStatus: string }> } };
      const projects = args.baseline?.projects ?? [];
      const scored = projects.map((p, i) => ({
        projectId: p.projectId,
        expectedStatus: p.expectedStatus,
        actualStatus: i % 4 === 3 ? 'on_track' : p.expectedStatus,
        statusMatch: i % 4 !== 3,
        citationHitRatio: 0.85,
        blockerHitRatio: 0.78,
        latencyMs: 120 + i * 30,
        notes: i % 4 === 3 ? ['status mismatch: mock fallback'] : [],
      }));
      const n = scored.length || 1;
      const statusAcc = (scored.filter((s) => s.statusMatch).length / n) * 100;
      const citAcc = (scored.reduce((a, s) => a + s.citationHitRatio, 0) / n) * 100;
      const blockerAcc = (scored.reduce((a, s) => a + s.blockerHitRatio, 0) / n) * 100;
      const lats = scored.map((s) => s.latencyMs);
      const maxLat = lats.length ? Math.max(...lats) : 0;
      const meanLat = lats.length ? lats.reduce((a, x) => a + x, 0) / lats.length : 0;
      return {
        projects: scored,
        totalProjects: scored.length,
        statusAccuracyPercent: statusAcc,
        citationAccuracyPercent: citAcc,
        blockerAccuracyPercent: blockerAcc,
        maxLatencyMs: maxLat,
        meanLatencyMs: meanLat,
        passesStatusBar: statusAcc >= 80,
        passesCitationBar: citAcc >= 80,
        passesLatencyBar: maxLat <= 2000,
        passesAll: statusAcc >= 80 && citAcc >= 80 && maxLat <= 2000,
      } as T;
    }
    case IPC.PILOT_DEMO_RESET: {
      await new Promise((r) => setTimeout(r, 1200));
      const now = new Date().toISOString();
      return {
        startedAt: now,
        completedAt: now,
        durationMs: 1180,
        steps: [
          { name: 'backup',         ok: true, durationMs: 65 },
          { name: 'reset',          ok: true, durationMs: 80 },
          { name: 'migrate',        ok: true, durationMs: 30 },
          { name: 'seedCanonical',  ok: true, durationMs: 120 },
          { name: 'seedPilot',      ok: true, durationMs: 820 },
          { name: 'integrityCheck', ok: true, durationMs: 25 },
          { name: 'audit',          ok: true, durationMs: 40 },
        ],
        backupPath: '/mock/backups/pre-pilot-reset-mock.db',
        integrityOk: true,
        counts: {
          buildings: 10, floors: 42, rooms: 264, telecomSpaces: 52,
          racks: 114, rackUnits: 798, assets: 798,
          switches: 96, patchPanels: 132, cablePaths: 10,
          copperCables: 114, projects: 6, documents: 24, clashTests: 8,
        },
      } as T;
    }
    case IPC.SETTINGS_HARD_RESET: {
      await new Promise((r) => setTimeout(r, 800));
      const now = new Date().toISOString();
      return {
        startedAt: now,
        completedAt: now,
        durationMs: 780,
        steps: [
          { name: 'backup',         ok: true, durationMs: 60 },
          { name: 'reset',          ok: true, durationMs: 90 },
          { name: 'migrate',        ok: true, durationMs: 30 },
          { name: 'seedCanonical',  ok: true, durationMs: 110 },
          { name: 'integrityCheck', ok: true, durationMs: 20 },
          { name: 'audit',          ok: true, durationMs: 35 },
        ],
        backupPath: '/mock/backups/pre-hard-reset-mock.db',
        integrityOk: true,
        counts: {},
      } as T;
    }
    case IPC.DIAGNOSTICS_DB_INFO: {
      return {
        dbPath: '/mock/data/db/bim-platform.db (web-preview mock)',
        exists: true,
        sizeBytes: 0,
        lastModifiedISO: new Date().toISOString(),
        sqliteVersion: '3.46.0',
        walEnabled: true,
        pragmas: { journalMode: 'wal', foreignKeys: 1, userVersion: 0 },
        counts: { projects: 0, buildings: 0, assets: 0, racks: 0, documents: 0, users: 1, audit_trail: 0 },
      } as T;
    }
    case IPC.AI_CHAT_STREAM: {
      // Simulate streaming in web preview — fire push events
      const streamId = (_data as { streamId?: string } | undefined)?.streamId ?? 'mock';
      void (async () => {
        const reply = 'I\'m running in web preview mode — wire up an Electron build (or set OPENAI_API_KEY / EMERGENT_LLM_KEY) to talk to a real provider.';
        for (const ch of reply.split('')) {
          await new Promise((r) => setTimeout(r, 15));
          window.dispatchEvent(new CustomEvent('__mock_ai_delta', { detail: { streamId, content: ch } }));
        }
        window.dispatchEvent(new CustomEvent('__mock_ai_done', { detail: { streamId, success: true } }));
      })();
      return { streamId, initiated: true } as T;
    }
    // ----- B4 Suggest-Mode Email (web preview) ------------------------------
    case IPC.EMAIL_LIST_TRIGGERS: {
      return {
        triggers: MOCK_B4_REGISTERED.map((t) => ({
          ...t, enabled: mockB4.triggerEnabled[t.key] ?? true,
        })),
        killSwitchOn: mockB4.killSwitchOn,
      } as T;
    }
    case IPC.EMAIL_SET_TRIGGER_ENABLED: {
      const { triggerKey, enabled } = _data as { triggerKey: string; enabled: boolean };
      mockB4.triggerEnabled[triggerKey] = enabled;
      return { ok: true } as T;
    }
    case IPC.EMAIL_GET_KILL_SWITCH: return { on: mockB4.killSwitchOn } as T;
    case IPC.EMAIL_SET_KILL_SWITCH: {
      const { on } = _data as { on: boolean };
      mockB4.killSwitchOn = on;
      return { on } as T;
    }
    case IPC.EMAIL_RUN_TRIGGER: {
      const args = _data as {
        projectId: string; triggerKey: string;
        defaultRecipient: { email: string; name?: string };
      };
      if (!(mockB4.triggerEnabled[args.triggerKey] ?? true)) {
        throw new IpcCallError({ code: 'B4_TRIGGER_DISABLED',
          message: `Trigger '${args.triggerKey}' is disabled for this tenant.` });
      }
      // Mock fixture: each invocation produces 1–2 deterministic drafts per
      // trigger; re-running de-duplicates by contentHash so the UI flow
      // mirrors the real backend.
      const samples: Record<string, Array<{ subject: string; body: string }>> = {
        'overdue-task-followup': [{
          subject: `Action needed — task T-101 12 days overdue on ${args.projectId}`,
          body: `Task T-101 on project ${args.projectId} is 12 days overdue. Please confirm next steps.`,
        }, {
          subject: `Action needed — task T-102 5 days overdue on ${args.projectId}`,
          body: `Task T-102 on project ${args.projectId} is 5 days overdue. Please confirm next steps.`,
        }],
        'overdue-rfi-followup': [{
          subject: `Action needed — RFI RFI-005 open for 14 days on ${args.projectId}`,
          body: `RFI-005 is still open. Please respond or escalate.`,
        }],
        'stalled-required-info-followup': [{
          subject: `Action needed — 'load-letter' missing for 11 days on ${args.projectId}`,
          body: `Required info 'load-letter' is still missing.`,
        }],
        'pre-closeout-milestone-checkin': [{
          subject: `Pre-closeout check-in — ${args.projectId} is 85% complete`,
          body: `Project is crossing the closeout threshold. Align on punch-list.`,
        }],
      };
      const candidates = samples[args.triggerKey] ?? [];
      const existing = new Set(mockB4.drafts
        .filter((d) => d.triggerKey === args.triggerKey && d.status !== 'discarded')
        .map((d) => d.contentHash));
      let skipped = 0;
      const createdDraftIds: string[] = [];
      for (const c of candidates) {
        const hash = mockHash(c.subject + '\n' + c.body);
        if (existing.has(hash)) { skipped += 1; continue; }
        const d = mockCreateDraft({
          projectId: args.projectId, triggerKey: args.triggerKey,
          subject: c.subject, body: c.body,
          defaultRecipient: args.defaultRecipient,
        });
        createdDraftIds.push(d.id);
      }
      return { createdDraftIds, skippedDuplicates: skipped, triggerKey: args.triggerKey } as T;
    }
    case IPC.EMAIL_LIST_DRAFTS: {
      const { status } = (_data as { status?: MockDraft['status'] } | undefined) ?? {};
      const items = status ? mockB4.drafts.filter((d) => d.status === status) : mockB4.drafts;
      return items as T;
    }
    case IPC.EMAIL_GET_DRAFT: {
      const { draftId } = _data as { draftId: string };
      return (mockB4.drafts.find((d) => d.id === draftId) ?? null) as T;
    }
    case IPC.EMAIL_UPDATE_DRAFT: {
      const { draftId, subject, body, recipients, attachments } = _data as {
        draftId: string; subject?: string; body?: string;
        recipients?: Array<{ email: string; name?: string }>;
        attachments?: Array<{ ref: string }>;
      };
      const d = mockB4.drafts.find((x) => x.id === draftId);
      if (!d) throw new IpcCallError({ code: 'NOT_FOUND', message: 'Draft not found' });
      if (d.status !== 'draft') {
        throw new IpcCallError({ code: 'EMAIL_DRAFT_INVALID_TRANSITION',
          message: `Cannot edit a '${d.status}' draft.` });
      }
      if (subject     !== undefined) d.subject     = subject;
      if (body        !== undefined) d.body        = body;
      if (recipients  !== undefined) d.recipients  = recipients;
      if (attachments !== undefined) d.attachments = attachments;
      d.contentHash = mockHash(d.subject + '\n' + d.body);
      d.updatedAt   = new Date().toISOString();
      return d as T;
    }
    case IPC.EMAIL_APPROVE_AND_SEND: {
      const { draftId } = _data as { draftId: string };
      if (mockB4.killSwitchOn) {
        throw new IpcCallError({ code: 'B4_SEND_KILL_SWITCH',
          message: 'Outbound email is disabled by the tenant kill-switch.' });
      }
      const d = mockB4.drafts.find((x) => x.id === draftId);
      if (!d) throw new IpcCallError({ code: 'NOT_FOUND', message: 'Draft not found' });
      if (d.status !== 'draft') {
        throw new IpcCallError({ code: 'EMAIL_DRAFT_INVALID_TRANSITION',
          message: `Cannot approve a '${d.status}' draft.` });
      }
      d.status      = 'sent';
      d.approvedBy  = 'demo';
      d.providerRef = `file:///mock/outbox/${d.id}.eml`;
      d.sentAt      = new Date().toISOString();
      d.updatedAt   = d.sentAt;
      return d as T;
    }
    case IPC.EMAIL_DISCARD_DRAFT: {
      const { draftId } = _data as { draftId: string };
      const d = mockB4.drafts.find((x) => x.id === draftId);
      if (!d) throw new IpcCallError({ code: 'NOT_FOUND', message: 'Draft not found' });
      if (d.status === 'sent') {
        throw new IpcCallError({ code: 'EMAIL_DRAFT_INVALID_TRANSITION',
          message: `Cannot discard a sent draft.` });
      }
      d.status    = 'discarded';
      d.updatedAt = new Date().toISOString();
      return d as T;
    }
    // ----- B5 Autopilot (web preview, plan-only) ----------------------------
    case IPC.AUTOPILOT_PLAN_CREATE: {
      const args = _data as { projectId: string | null; goal: string; now?: string };
      const proposals = mockProposeActions(args.projectId);
      const now = args.now ?? new Date().toISOString();
      const planId = `mock-plan-${mockAP.seq++}`;
      const actions: MockAction[] = proposals.map((p, i) => ({
        ...p,
        id:        `mock-action-${mockAP.seq++}-${i}`,
        planId,
        tenantId:  'default',
        status:    'proposed' as const,
        outcome:   null,
        createdAt: now,
        updatedAt: now,
      }));
      const plan: MockPlan = {
        id: planId, tenantId: 'default',
        projectId: args.projectId, actorId: 'demo',
        goal: args.goal,
        rationale: proposals.length === 0
          ? 'No capability could contribute an action for this goal.'
          : `Composed plan with ${proposals.length} action(s) across `
            + `${new Set(proposals.map((p) => p.capability)).size} capabilit${proposals.length === 1 ? 'y' : 'ies'}.`,
        status: 'proposed',
        actions,
        createdAt: now, updatedAt: now,
      };
      mockAP.plans.unshift(plan);
      return plan as T;
    }
    case IPC.AUTOPILOT_PLAN_LIST: return mockAP.plans as T;
    case IPC.AUTOPILOT_PLAN_PREVIEW: {
      const { planId } = _data as { planId: string };
      const p = mockAP.plans.find((x) => x.id === planId);
      if (!p) throw new IpcCallError({ code: 'NOT_FOUND', message: 'Plan not found' });
      const summary: Record<string, number> = {};
      const actions = p.actions.map((a) => {
        summary[a.capability] = (summary[a.capability] ?? 0) + 1;
        return {
          actionId:    a.id,
          capability:  a.capability,
          title:       a.title,
          description: a.description,
          status:      a.status,
          willHappen:  describeMockAction(a),
          executable:  true,
        };
      });
      return { planId: p.id, rationale: p.rationale, actions, capabilitySummary: summary } as T;
    }
    case IPC.AUTOPILOT_PLAN_APPROVE: {
      const input = _data as {
        actionId?: string; actionIds?: string[]; planId?: string;
        scope: 'once' | 'session' | 'always'; sessionId?: string;
      };
      let targets: MockAction[] = [];
      if (input.actionId) {
        for (const p of mockAP.plans) {
          const a = p.actions.find((x) => x.id === input.actionId);
          if (a) { targets = [a]; break; }
        }
      } else if (input.planId) {
        const plan = mockAP.plans.find((x) => x.id === input.planId);
        if (plan) targets = plan.actions;
      } else if (input.actionIds && input.actionIds.length > 0) {
        const wanted = new Set(input.actionIds);
        for (const p of mockAP.plans) {
          for (const a of p.actions) if (wanted.has(a.id)) targets.push(a);
        }
      }
      const approvedActionIds: string[] = [];
      let skipped = 0;
      for (const a of targets) {
        if (a.status !== 'proposed') { skipped += 1; continue; }
        a.status    = 'approved';
        a.updatedAt = new Date().toISOString();
        approvedActionIds.push(a.id);
      }
      return { approvedActionIds, skipped } as T;
    }
    case IPC.AUTOPILOT_PLAN_REJECT: {
      const { actionId } = _data as { actionId: string };
      for (const p of mockAP.plans) {
        const a = p.actions.find((x) => x.id === actionId);
        if (a) {
          a.status    = 'rejected';
          a.updatedAt = new Date().toISOString();
          return a as T;
        }
      }
      throw new IpcCallError({ code: 'NOT_FOUND', message: 'Action not found' });
    }
    // ----- B6 Agent Projects (web preview) ----------------------------------
    case IPC.AGENT_LIST_TEMPLATES: return MOCK_AGENT_TEMPLATES as T;
    case IPC.AGENT_LIST_GOALS:     return mockAG.goals as T;
    case IPC.AGENT_GET_GOAL: {
      const { goalId } = _data as { goalId: string };
      const goal = mockAG.goals.find((g) => g.id === goalId);
      if (!goal) return null as T;
      const plan = goal.planId ? (mockAP.plans.find((p) => p.id === goal.planId) ?? null) : null;
      const progress = mockAG.events.get(goalId) ?? [];
      return { goal, plan, progress } as T;
    }
    case IPC.AGENT_REGISTER_GOAL: {
      const input = _data as {
        projectId: string | null; templateKey: string | null;
        goalText: string; now?: string;
      };
      // Cross-tenant guard: reject if goal text references another tenant.
      const tenantToken = /\btenant[_= -]([a-z0-9-]+)/gi;
      let m: RegExpExecArray | null;
      while ((m = tenantToken.exec(input.goalText)) !== null) {
        if (m[1] && m[1].toLowerCase() !== 'default') {
          throw new IpcCallError({ code: 'B6_CROSS_TENANT_GOAL',
            message: 'Goal text references a tenant other than the caller — rejected.' });
        }
      }
      const now = input.now ?? new Date().toISOString();
      const goalId = `mock-goal-${mockAG.seq++}`;
      const proposals = mockProposeActions(input.projectId);

      // Build a B5 plan via the same mock path the autopilot uses.
      const planId = `mock-plan-${mockAP.seq++}`;
      const actions: MockAction[] = proposals.map((p, i) => ({
        ...p,
        id:        `mock-action-${mockAP.seq++}-${i}`,
        planId, tenantId: 'default',
        status:    'proposed' as const, outcome: null,
        createdAt: now, updatedAt: now,
      }));
      const plan: MockPlan = {
        id: planId, tenantId: 'default',
        projectId: input.projectId, actorId: 'demo',
        goal: input.goalText,
        rationale: proposals.length === 0
          ? 'Agent: no capability surfaced an action for this goal.'
          : `Agent decomposed the goal into ${proposals.length} step(s) across ${new Set(proposals.map((p) => p.capability)).size} capabilit${proposals.length === 1 ? 'y' : 'ies'}.`,
        status: 'proposed', actions,
        createdAt: now, updatedAt: now,
      };
      mockAP.plans.unshift(plan);

      const goal: MockGoal = {
        id: goalId, tenantId: 'default', actorId: 'demo',
        projectId: input.projectId,
        templateKey: input.templateKey,
        goalText: input.goalText,
        status: actions.length === 0 ? 'done' : 'awaiting_approval',
        planId,
        cancelRequested: false,
        createdAt: now, updatedAt: now,
      };
      mockAG.goals.unshift(goal);
      appendMockEvent(goalId, 'plan_created',
        `Plan created with ${actions.length} step(s).`,
        { planId, rationale: plan.rationale, steps: actions.length });
      if (actions.length === 0) appendMockEvent(goalId, 'goal_done',
        `Goal terminated in status 'done'.`);
      return { goal, plan, progress: mockAG.events.get(goalId) ?? [] } as T;
    }
    case IPC.AGENT_APPROVE_AND_RUN: {
      const input = _data as {
        goalId: string; scope: 'once' | 'session' | 'always'; sessionId?: string;
      };
      const goal = mockAG.goals.find((g) => g.id === input.goalId);
      if (!goal) throw new IpcCallError({ code: 'NOT_FOUND', message: 'Goal not found' });
      if (goal.status !== 'awaiting_approval' && goal.status !== 'failed') {
        throw new IpcCallError({ code: 'B6_INVALID_STATE',
          message: `Goal is in '${goal.status}' state — operation not permitted.` });
      }
      goal.status = 'running'; goal.updatedAt = new Date().toISOString();
      appendMockEvent(goal.id, 'awaiting_approval',
        `Operator granted '${input.scope}' approval; agent starting plan execution.`,
        { scope: input.scope });

      const plan = mockAP.plans.find((p) => p.id === goal.planId);
      if (!plan) throw new IpcCallError({ code: 'NOT_FOUND', message: 'Plan vanished mid-run' });

      // Approve every still-proposed action.
      for (const a of plan.actions) {
        if (a.status === 'proposed') {
          a.status    = 'approved';
          a.updatedAt = new Date().toISOString();
        }
      }

      // Run sequentially. Honour cancelRequested between steps.
      let cancelledMid = false;
      for (const a of plan.actions) {
        if (goal.cancelRequested) { cancelledMid = true;
          appendMockEvent(goal.id, 'cancel_requested',
            `Cancel requested — skipping remaining step(s).`, undefined, a.id); break; }
        if (a.status !== 'approved') continue;
        appendMockEvent(goal.id, 'step_started',
          `Running '${a.title}' via capability '${a.capability}'.`, undefined, a.id);
        a.status    = 'done';
        a.outcome   = { mock: true, capability: a.capability };
        a.updatedAt = new Date().toISOString();
        appendMockEvent(goal.id, 'step_done',
          `Completed '${a.title}'.`, { outcome: a.outcome }, a.id);
      }
      const finalStatus = cancelledMid ? 'cancelled' : 'done';
      goal.status    = finalStatus;
      goal.updatedAt = new Date().toISOString();
      appendMockEvent(goal.id,
        finalStatus === 'cancelled' ? 'goal_cancelled' : 'goal_done',
        `Goal terminated in status '${finalStatus}'.`);

      const progress = mockAG.events.get(goal.id) ?? [];
      return { goal, plan, progress } as T;
    }
    case IPC.AGENT_CANCEL_GOAL: {
      const { goalId } = _data as { goalId: string };
      const goal = mockAG.goals.find((g) => g.id === goalId);
      if (!goal) throw new IpcCallError({ code: 'NOT_FOUND', message: 'Goal not found' });
      goal.cancelRequested = true;
      goal.updatedAt = new Date().toISOString();
      appendMockEvent(goal.id, 'cancel_requested',
        'Operator requested cancel — agent will stop after the current step.');
      const plan = goal.planId ? (mockAP.plans.find((p) => p.id === goal.planId) ?? null) : null;
      const progress = mockAG.events.get(goal.id) ?? [];
      return { goal, plan, progress } as T;
    }
    // ----- B8 Company Cost Templates (web preview) --------------------------
    case IPC.COST_LIST_LINEAGES: return mockB8Lineages() as T;
    case IPC.COST_LIST_VERSIONS: {
      const { templateKey } = _data as { templateKey: string };
      return mockB8.templates.filter((t) => t.templateKey === templateKey)
        .sort((a, b) => a.version - b.version) as T;
    }
    case IPC.COST_GET_TEMPLATE: {
      const { id } = _data as { id: string };
      const tpl = mockB8.templates.find((t) => t.id === id);
      if (!tpl) throw new IpcCallError({ code: 'B8_TEMPLATE_NOT_FOUND',
        message: `Cost template not found: ${id}` });
      return tpl as T;
    }
    case IPC.COST_IMPORT_JSON: {
      const { raw } = _data as { raw: string };
      let parsed: Record<string, unknown>;
      try { parsed = JSON.parse(raw) as Record<string, unknown>; }
      catch (err) { throw new IpcCallError({ code: 'B8_JSON_IMPORT_ERROR',
        message: `Invalid JSON — ${(err as Error).message}` }); }
      const required = ['templateKey', 'name', 'labor', 'materials',
                        'equipment', 'subcontractors', 'markup'];
      for (const k of required) if (!(k in parsed)) {
        throw new IpcCallError({ code: 'B8_JSON_IMPORT_ERROR',
          message: `Missing required field '${k}'` });
      }
      const key = String(parsed['templateKey']);
      const existing = mockB8.templates.filter((t) => t.templateKey === key);
      const version = existing.length === 0
        ? 1 : Math.max(...existing.map((t) => t.version)) + 1;
      const snap = {
        schemaVersion: 1,
        templateKey:   key,
        version,
        name:          String(parsed['name']),
        description:   String(parsed['description'] ?? ''),
        region:        String(parsed['region'] ?? 'default'),
        estimateClass: String(parsed['estimateClass'] ?? 'ROM').toUpperCase()
                         .replace('DETAILED', 'detailed'),
        labor:         (parsed['labor']         ?? []) as unknown[],
        materials:     (parsed['materials']     ?? []) as unknown[],
        equipment:     (parsed['equipment']     ?? []) as unknown[],
        subcontractors:(parsed['subcontractors']?? []) as unknown[],
        markup:        parsed['markup']         as unknown,
        rulePack:      (parsed['rulePack'] ?? {}) as unknown,
        currency:      String(parsed['currency'] ?? 'USD'),
      };
      const id = `mock-tpl-${mockB8.seq++}`;
      const now = new Date().toISOString();
      const tpl = {
        id, templateKey: key, tenantId: 'default', version,
        name:          snap.name, description: snap.description,
        region:        snap.region,
        estimateClass: snap.estimateClass,
        isActive:      true,
        createdBy:     'demo', createdAt: now,
        snapshot:      snap,
      };
      mockB8.templates.push(tpl);
      return tpl as T;
    }
    case IPC.COST_ESTIMATE_ON_TEMPLATE: {
      const args = _data as {
        templateVersionId: string;
        projectId?: string | null;
        scope: { estimateClass?: string; notes?: string;
          lines: Array<{ ref: string; category: 'labor'|'material'|'equipment'|'subcontractor';
            identifier: string; quantity: number; aiAssisted?: boolean }>;
        };
      };
      const tpl = mockB8.templates.find((t) => t.id === args.templateVersionId);
      if (!tpl) throw new IpcCallError({ code: 'B8_TEMPLATE_NOT_FOUND',
        message: `Cost template not found: ${args.templateVersionId}` });
      const { estimate, record } = mockB8ComputeEstimate(tpl, args.scope, args.projectId ?? null);
      mockB8.estimates.unshift(record);
      return { record, estimate } as T;
    }
    case IPC.COST_LIST_ESTIMATES: return mockB8.estimates as T;
    case IPC.COST_GET_BOUND: {
      const { projectId } = _data as { projectId: string };
      const active = mockB8.bindings.find((b) =>
        b.projectId === projectId && b.unboundAt === null);
      if (!active) return null as T;
      return (mockB8.templates.find((t) => t.id === active.templateVersionId) ?? null) as T;
    }
    case IPC.COST_GET_ACTIVE_BINDING: {
      const { projectId } = _data as { projectId: string };
      return (mockB8.bindings.find((b) =>
        b.projectId === projectId && b.unboundAt === null) ?? null) as T;
    }
    case IPC.COST_LIST_BINDING_HISTORY: {
      const { projectId } = _data as { projectId: string };
      const rows = mockB8.bindings.filter((b) => b.projectId === projectId);
      rows.sort((a, b) => a.boundAt.localeCompare(b.boundAt));
      return rows as T;
    }
    case IPC.COST_BIND_PROJECT: {
      const { projectId, templateVersionId } = _data as {
        projectId: string; templateVersionId: string;
      };
      const tpl = mockB8.templates.find((t) => t.id === templateVersionId);
      if (!tpl) throw new IpcCallError({ code: 'B8_TEMPLATE_NOT_FOUND',
        message: `Cost template not found: ${templateVersionId}` });
      const now = new Date().toISOString();
      // Close any active binding first.
      for (const b of mockB8.bindings) {
        if (b.projectId === projectId && b.unboundAt === null) b.unboundAt = now;
      }
      const binding: MockProjectBinding = {
        id: `mock-bind-${mockB8.seq++}`,
        tenantId: 'default', projectId,
        templateVersionId,
        boundBy: 'demo', boundAt: now, unboundAt: null,
      };
      mockB8.bindings.push(binding);
      return binding as T;
    }
    case IPC.COST_UNBIND_PROJECT: {
      const { projectId } = _data as { projectId: string };
      const now = new Date().toISOString();
      let closed = 0;
      for (const b of mockB8.bindings) {
        if (b.projectId === projectId && b.unboundAt === null) {
          b.unboundAt = now; closed++;
        }
      }
      return { closed } as T;
    }
    case IPC.COST_ESTIMATE_FOR_PROJECT: {
      const args = _data as { projectId: string;
        scope: { estimateClass?: string; notes?: string;
          lines: Array<{ ref: string; category: 'labor'|'material'|'equipment'|'subcontractor';
            identifier: string; quantity: number; aiAssisted?: boolean }>;
        };
      };
      const active = mockB8.bindings.find((b) =>
        b.projectId === args.projectId && b.unboundAt === null);
      if (!active) throw new IpcCallError({ code: 'B8_NO_BINDING',
        message: `Project '${args.projectId}' has no active cost template binding.` });
      const tpl = mockB8.templates.find((t) => t.id === active.templateVersionId);
      if (!tpl) throw new IpcCallError({ code: 'B8_TEMPLATE_NOT_FOUND',
        message: `Bound template missing: ${active.templateVersionId}` });
      const { estimate, record } = mockB8ComputeEstimate(tpl, args.scope, args.projectId);
      mockB8.estimates.unshift(record);
      return { record, estimate } as T;
    }
    case IPC.COST_SUGGEST_SCOPE: {
      const args = _data as { templateVersionId: string; goalText: string };
      const tpl = mockB8.templates.find((t) => t.id === args.templateVersionId);
      if (!tpl) throw new IpcCallError({ code: 'B8_TEMPLATE_NOT_FOUND',
        message: `Cost template not found: ${args.templateVersionId}` });
      return mockB8Suggest(tpl, args.goalText) as T;
    }
    default: return [] as unknown as T;
  }
}

// ---------------------------------------------------------------------------
// B7 — Locale mock store and handler (web preview).
// ---------------------------------------------------------------------------
const mockB7: { preference: string | null } = { preference: null };

function handleB7MockChannel(channel: string, data: unknown): unknown | undefined {
  switch (channel) {
    case IPC.LOCALE_LIST: return [
      { code: 'en',  displayName: 'English',  nativeName: 'English' },
      { code: 'fr',  displayName: 'French',   nativeName: 'Français' },
      { code: 'es',  displayName: 'Spanish',  nativeName: 'Español' },
      { code: 'de',  displayName: 'German',   nativeName: 'Deutsch' },
      { code: 'fil', displayName: 'Filipino', nativeName: 'Filipino' },
    ];
    case IPC.LOCALE_GET_PREFERENCE: {
      if (!mockB7.preference) return null;
      return {
        userId: 'demo', tenantId: 'default',
        locale: mockB7.preference,
        updatedAt: new Date().toISOString(),
      };
    }
    case IPC.LOCALE_SET_PREFERENCE: {
      const { locale } = data as { locale: string };
      const ok = ['en','fr','es','de','fil'].includes(locale);
      if (!ok) throw new IpcCallError({
        code: 'B7_UNSUPPORTED_LOCALE', message: `Unsupported locale: ${locale}`,
      });
      mockB7.preference = locale;
      return { userId: 'demo', tenantId: 'default',
               locale, updatedAt: new Date().toISOString() };
    }
    case IPC.LOCALE_TRANSLATE: {
      const args = data as { text: string; sourceLang: string; targetLang: string };
      const ok = ['en','fr','es','de','fil'].includes(args.targetLang);
      if (!ok) throw new IpcCallError({
        code: 'B7_UNSUPPORTED_LOCALE', message: `Unsupported locale: ${args.targetLang}`,
      });
      const same = args.sourceLang === args.targetLang;
      return {
        text: args.text, providerKey: 'offline-fallback',
        fellBackToEnglish: !same && args.targetLang !== 'en',
      };
    }
    case IPC.LOCALE_PROVIDER_INFO:
      return { providerKey: 'offline-fallback',
               displayName: 'Offline fallback (no remote translator)',
               isLive: false };
    default: return undefined;
  }
}

// FRIS — web preview mock data
// ---------------------------------------------------------------------------
function buildFrisMock() {
  const now = Date.now();
  return [
    {
      id: 'sie', label: 'Smart Ingestion Engine', service: 'SIE Phase 1',
      status: 'green', reason: 'All extractors loaded, SQLite backend ready.',
      dependencies: [
        { id: 'db', label: 'SQLite Connection', ok: true },
        { id: 'extractors', label: 'Regex Extractors', ok: true },
        { id: 'drizzle', label: 'Drizzle ORM', ok: true },
      ],
      setupSteps: [], lastChecked: now - 4_000,
    },
    {
      id: 'calendar', label: 'Calendar Engine', service: 'Scheduling · CalDAV',
      status: 'green', reason: 'Local calendar operational. M365 sync not configured.',
      dependencies: [
        { id: 'db', label: 'SQLite Connection', ok: true },
        { id: 'ical', label: 'iCal Parser', ok: true },
      ],
      setupSteps: [], lastChecked: now - 6_000,
    },
    {
      id: 'm365', label: 'M365 Sync Engine', service: 'Microsoft Graph API',
      status: 'yellow', reason: 'OAuth token not configured. Read-only preview active.',
      dependencies: [
        { id: 'network', label: 'Network', ok: true },
        { id: 'oauth',   label: 'OAuth2 Token', ok: false, reason: 'Not configured — run M365 Auth setup.' },
      ],
      setupSteps: ['Open Settings → Sync → Microsoft 365', 'Click Connect & authenticate with your Microsoft account', 'Grant Calendar + Contacts read permissions'],
      setupRoute: 'sync',
      lastChecked: now - 8_000,
    },
    {
      id: 'fris', label: 'FRIS Observer', service: 'Observability Plane',
      status: 'green', reason: 'All probes registered. Polling every 30s.',
      dependencies: [
        { id: 'registry', label: 'Probe Registry', ok: true },
      ],
      setupSteps: [], lastChecked: now - 2_000,
    },
    {
      id: 'pfe', label: 'Project File Explorer', service: 'PFE · Node.js fs',
      status: 'green', reason: 'File watcher active. 0 stale paths.',
      dependencies: [
        { id: 'fs', label: 'File System Access', ok: true },
        { id: 'watcher', label: 'fs.watch()', ok: true },
      ],
      setupSteps: [], lastChecked: now - 5_000,
    },
    {
      id: 'license', label: 'License Gate', service: 'RCDD Verification',
      status: 'green', reason: '14-day trial active.',
      dependencies: [
        { id: 'db', label: 'SQLite Connection', ok: true },
      ],
      setupSteps: [], lastChecked: now - 3_000,
    },
    {
      id: 'db', label: 'SQLite Database', service: 'Drizzle ORM · better-sqlite3',
      status: 'green', reason: 'DB connection healthy. 0 migration drift.',
      dependencies: [
        { id: 'fs', label: 'File System Write', ok: true },
        { id: 'migrations', label: 'Schema up-to-date', ok: true },
      ],
      setupSteps: [], lastChecked: now - 1_000,
    },
  ];
}
